import axios, { AxiosInstance } from 'axios';
import config from '../config/environment';

// Types for API responses
export interface File {
  id: string;
  user_id: string;
  device_id: string;
  file_name: string;
  encryption_type: string;
  sync_status: string;
  status: 'processing' | 'ready' | 'failed' | 'blocked'; // File status for access control
  created_at: string;
  last_synced_at?: string;
  asset_permissions_id: string; // Updated from policy_id
  asset_type: 'file' | 'folder';
  owner_email?: string;
  recipients: FileRecipient[];
  recipient_count: number;
  metadata?: {
    id: string;
    file_size: number;
    mime_type: string;
    hash: string;
  };
}

export interface FileRecipient {
  id: string;
  user_id: string; // Updated to match backend structure
  times_accessible: number;
  total_accesses: number;
  expires_at?: string;
  last_accessed_at?: string;
  value: string; // Email address for the recipient
  type: string; // Type of recipient (e.g., 'email')
  is_external: boolean; // Whether this is an external recipient
  created_at: string; // When the recipient was added
}

export interface SharedFile {
  id: string;
  file_name: string;
  asset_type?: 'file' | 'folder';
  shared_at: string;
  allowed_access_from?: string;
  allowed_access_to?: string;
  times_accessible?: number;
  total_accesses: number;
  owner_email: string;
  allowed_permissions: { [key: string]: boolean };
  created_at?: string;
  mime_type?: string; // ADDED: MIME type for icon display
  file_size?: number; // ADDED: File size for display
}

export interface Policy {
  id: string;
  name: string;
  description: string;
  type: 'system' | 'company' | 'user';
  asset_type: 'file' | 'folder';
  status: 'active' | 'inactive' | 'draft';
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  settings: PolicySettings;
}

export interface AssetPermissions {
  id: string;
  policy_id: string;
  asset_type: 'file' | 'folder';
  created_at: string;
  updated_at: string;
  created_by: string;
  settings: PolicySettings; // Same structure as Policy settings
}

export interface Invitation {
  id: string;
  company_id: string;
  inviter_id: string;
  invitee_email: string;
  role: string;
  mode: string;
  status: string;
  expires_at: string;
  created_at: string;
  /** Present when listing; used to build accept link for copy */
  token?: string;
}

export interface CompanyUsagesResponse {
  company_id: string;
  company_name: string;
  full_licenses_used: number;
  full_licenses_max: number;
  reader_licenses_used: number;
  /** Active reader licenses held by non–company_auditor users (reader invite pool). */
  reader_pool_licenses_used?: number;
  reader_licenses_max: number;
  auditor_licenses_used?: number;
  auditor_licenses_max?: number | null;
  licenses: Array<{
    id: string;
    subscription_id: string;
    user_id: string;
    user_email?: string;
    company_id?: string;
    type: string;
    status: string;
    revoked_at?: string;
    created_at: string;
    updated_at: string;
  }>;
  usage_rows: {
    user_id: string;
    user_email: string;
    devices_count: number;
    folders_count: number;
    invites_sent_full: number;
    invites_sent_reader: number;
  }[];
}

export interface PolicySettings {
  encryption_type: string;
  recipient_settings?: RecipientSettings;
  file_permissions?: FilePermissions;
  folder_permissions?: FolderPermissions;
  screen_watermark: boolean;
  print_watermark: boolean;
  security: SecuritySettings;
  access_limitations?: AccessLimitations;
  delete_on_limit_exceeded?: boolean;
  recipient_message: string;
}

export interface RecipientSettings {
  allowed_domains: string[];
  allowed_subdomains: string[];
  require_approval: boolean;
}

export interface FilePermissions {
  convert_original: OperationPermission;
  save_changes: OperationPermission;
  copy_paste: OperationPermission;
  screen_capture: OperationPermission;
  print: OperationPermission;
  share: OperationPermission;
}

export interface FolderPermissions {
  create: OperationPermission;
  copy: OperationPermission;
  add: OperationPermission;
  move: OperationPermission;
  share: OperationPermission;
  delete: OperationPermission;
}

export interface OperationPermission {
  allowed: boolean;
  audit_level: 'none' | 'basic' | 'detailed';
}

export interface WatermarkTextConfig {
  owner_company_name: boolean;
  recipient_company_name: boolean;
  owner_email: boolean;
  recipient_email: boolean;
  recipient_ip_address: boolean;
  timestamp: boolean;
  file_name: boolean;
  device_name: boolean;
  custom_text: boolean;
  custom_text_value: string;
}

export interface WatermarkSettings {
  text?: string; // Deprecated, use text_config instead
  text_config: WatermarkTextConfig;
  font: string;
  font_size: number;
  line_spacing?: number; // Optional - no longer saved/returned, fixed at 50mm in preview only
  color: string;
  opacity: number;
  angle: number; // Rotation angle in degrees (-90 to 90, where 0 is horizontal)
}

export interface SecuritySettings {
  auth_on_asset_open: boolean;
  authentication?: AuthenticationSettings;
  session?: SessionSettings;
  protection?: ProtectionSettings;
}

export interface AuthenticationSettings {
  two_factor: boolean;
  biometric: boolean;
  max_attempts: number;
}

export interface SessionSettings {
  timeout_seconds: number;
  allow_refresh: boolean;
}

export interface ProtectionSettings {
  delete_on_unauthorized: boolean;
  delete_on_limit_exceeded: boolean;
}

export interface AccessLimitations {
  access_count_limitation?: number;
  allowed_access_from?: string;
  allowed_access_to?: string;
}

export interface CreatePolicyRequest {
  name: string;
  description: string;
  type?: string;
  asset_type?: 'file' | 'folder';
  status?: 'active' | 'inactive' | 'draft';
  for_companies?: string[];
  settings: PolicySettings;
}

export interface Company {
  id: string;
  name: string;
  domain?: string;
  description?: string;
  website?: string;
  logo_url?: string;
  app_version?: string;
  contact_person?: string;
  contact_person_email?: string;
  contact_email?: string;
  address?: string;
  is_active?: boolean;
  total_licenses?: number;
  available_licenses?: number;
  created_at: string;
  updated_at: string;
}

/** Company with license availability (for create-user dropdown). Only companies with at least one available license. */
export interface CompanyWithLicenseAvailability extends Company {
  full_available: number;
  reader_available: number;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  role: 'user' | 'admin' | 'sysadmin' | string; // string allows translator_xx format
  company_id?: string;
}

export interface UpdateUserRequest {
  email?: string;
  name?: string;
  role?: 'user' | 'admin' | 'sysadmin' | string; // string allows translator_xx format
  mode?: 'full' | 'reader';
  company_id?: string | null; // Allow null to explicitly remove company assignment
  is_active?: boolean;
}

export interface RecipientRule {
  id: string;
  type: 'email' | 'domain' | 'subdomain' | 'user' | 'internal';
  value: string;
  is_wildcard: boolean;
}


export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  data?: any;
}

export interface AutoEncryptFolder {
  id: string;
  name?: string;
  description?: string;
  user_id: string;
  device_id: string;
  policy_id: string;
  folder_path: string;
  file_count: number;
  auto_delete_original: boolean;
  move_original_file?: boolean;
  move_original_to_path?: string;
  is_active: boolean;
  include_subfolders: boolean;
  // removed: file_extensions
  last_processed_at?: string;
  provisioning_state?: string;
  is_template?: boolean;
  auto_provision_to_members?: boolean;
  type?: string;
  company_id?: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateAutoEncryptFolderRequest {
  policy_id?: string;
  folder_path?: string;
  file_count?: number;
  auto_delete_original?: boolean;
  move_original_file?: boolean;
  move_original_to_path?: string;
  is_active?: boolean;
  include_subfolders?: boolean;
  auto_provision_to_members?: boolean; // templates only
}

export interface DashboardStats {
  secured_assets: number;
  monitored_folders: number;
  recent_notifications: Notification[];
  service_status: {
    folder_monitoring: 'active' | 'inactive';
    google_drive_integration: 'active' | 'inactive';
    one_drive_integration: 'active' | 'inactive';
  };
}

// Logging System Types
export interface AccessLogEntry {
  id: string;
  created_at: string;
  file_id: string;
  file_name: string;
  asset_permissions_id: string;
  policy_name: string;
  policy_type: string;
  user_id: string;
  user_email: string;
  user_company_id?: string;
  owner_user_id?: string;
  owner_email?: string;
  device_id: string;
  device_fingerprint?: string;
  device_name?: string;
  device_type?: string;
  ip_address: string;
  user_agent: string;
  location_country?: string;
  location_city?: string;
  operation: string;
  operation_type: string;
  access_granted: boolean;
  access_reason: string;
  violation_type?: string;
  session_token?: string;
  access_count_limit?: number;
  access_count_used: number;
  access_count_remaining?: number;
  time_window_start?: string;
  time_window_end?: string;
  audit_level: string;
  file_size: number;
  file_hash: string;
  encryption_type?: string;
}

export interface LogSearchQuery {
  start_date?: string;
  end_date?: string;
  file_ids?: string[];
  file_names?: string[];
  user_ids?: string[];
  user_emails?: string[];
  owner_emails?: string[];
  owner_user_ids?: string[];
  companies?: string[];
  company_id?: string;
  policy_ids?: string[];
  policy_types?: string[];
  device_ids?: string[];
  device_types?: string[];
  ip_addresses?: string[];
  operations?: string[];
  operation_types?: string[];
  access_granted?: boolean;
  violation_types?: string[];
  access_reasons?: string[];
  audit_levels?: string[];
  countries?: string[];
  cities?: string[];
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: string;
  group_by?: string[];
  aggregate?: string[];
}

export interface LogSearchResult {
  logs: AccessLogEntry[];
  total_count: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
}

export interface AccessStatistics {
  total_accesses: number;
  successful_accesses: number;
  failed_accesses: number;
  unique_files: number;
  unique_users: number;
  operation_breakdown: Record<string, number>;
  violations_breakdown: Record<string, number>;
  top_accessed_files: Array<{
    file_id: string;
    file_name: string;
    access_count: number;
  }>;
  top_violating_users: Array<{
    user_id: string;
    user_email: string;
    violation_count: number;
  }>;
  device_breakdown: Record<string, number>;
  location_breakdown: Record<string, number>;
  time_distribution?: Array<{
    period: string;
    count: number;
  }>;
}

export interface ViolationReport {
  total_violations: number;
  violation_types: Record<string, number>;
  operation_violations: Record<string, Record<string, number>>;
  top_violating_users: Array<{
    user_id: string;
    user_email: string;
    violation_count: number;
  }>;
  top_violated_files: Array<{
    file_id: string;
    file_name: string;
    violation_count: number;
  }>;
  policy_violations: Array<{
    policy_id: string;
    policy_name: string;
    violation_count: number;
  }>;
  device_violations?: Record<string, number>;
  location_violations?: Record<string, number>;
}


export interface LogExportRequest {
  format: 'csv' | 'json' | 'pdf';
  start_date?: string;
  end_date?: string;
  filters?: LogSearchQuery;
}

export interface LogExportResponse {
  export_id: string;
  status: 'processing' | 'completed' | 'failed';
  message?: string;
  download_url?: string;
  file_size?: number;
  record_count?: number;
  created_at?: string;
  completed_at?: string;
  progress?: number;
  estimated_completion?: string;
}

class ApiService {
  private api: AxiosInstance;
  private isRefreshing: boolean = false; // Prevent multiple simultaneous refresh attempts
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
    config: any;
  }> = []; // Queue for failed requests during refresh

  constructor() {
    // Debug logging
    console.log('Environment config:', {
      isDevelopment: config.isDevelopment,
      apiUrl: config.apiUrl,
      env: config.env,
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_ENV: process.env.REACT_APP_ENV
    });

    // Use relative URL in development (via proxy) and production (our custom server)
    const baseURL = '/api/v1';

    console.log('API baseURL:', baseURL);

    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // 🔒 CRITICAL: Send cookies with requests
    });

    // Add request interceptor (tokens and device info are in httpOnly cookies now)
    this.api.interceptors.request.use(
      async (config) => {
        // 🔒 Add CSRF token to all state-changing requests (POST, PUT, DELETE, PATCH)
        const method = config.method?.toUpperCase();
        if (method && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
          // Get CSRF token from cookie
          const csrfToken = this.getCookie('csrf_token');
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
            console.log(
              `%c🔒 CSRF: Token added`,
              'color: #00ff00; font-weight: bold;',
              `${method} ${config.url}`,
              `Token: ${csrfToken.substring(0, 16)}...`
            );
          } else {
            console.error(
              `%c⚠️ CSRF: NO TOKEN FOUND`,
              'color: #ff0000; font-weight: bold;',
              `${method} ${config.url}`,
              '\nAvailable cookies:', document.cookie
            );
            // Log all cookies for debugging
            const allCookies = document.cookie.split(';').map(c => c.trim().split('=')[0]);
            console.log('Cookie names:', allCookies);
          }
        }

        // 🔒 Device ID is now in httpOnly cookie, backend will read it automatically
        // No need to add X-Device-ID header from frontend
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors with automatic token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // 402 SUBSCRIPTION_EXPIRED — backend's LicenseValidityMiddleware refusing
        // an authenticated request because the requester's sub crossed expiry.
        // Surface it: stash the recipient-aware error message for /login to toast,
        // wipe local auth state, and bounce to /login. Skip if we're already on a
        // public auth page (no point bouncing to where we already are).
        if (
          error.response?.status === 402 &&
          error.response?.data?.code === 'SUBSCRIPTION_EXPIRED'
        ) {
          const msg =
            error.response?.data?.error ||
            'Your subscription has expired.';
          try {
            localStorage.setItem('postLogoutErrorMessage', msg);
            localStorage.setItem('postLogoutErrorReason', 'subscription_expired');
          } catch (_) {
            // localStorage may be unavailable in some embedded contexts; fall
            // through — Login.tsx will simply not see the message.
          }
          const currentPath = window.location.pathname;
          const isPublicAuthPage =
            currentPath === '/login' ||
            currentPath === '/forgot-password' ||
            currentPath === '/register' ||
            currentPath === '/register/verify-email' ||
            currentPath === '/accept-invite';
          if (!isPublicAuthPage) {
            // Preserve the error keys we just set; only clear auth-related items.
            // localStorage.clear() would wipe postLogoutErrorMessage too.
            try {
              const preserved: Record<string, string> = {};
              for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith('postLogout')) preserved[k] = localStorage.getItem(k) || '';
              }
              localStorage.clear();
              for (const [k, v] of Object.entries(preserved)) localStorage.setItem(k, v);
            } catch (_) {
              // ignore
            }
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Check if we're on a public auth page - don't retry or redirect (stay on login, register, forgot-password)
          const currentPath = window.location.pathname;
          const isPublicAuthPage = currentPath === '/login' || currentPath === '/forgot-password' || currentPath === '/register' || currentPath === '/register/verify-email' || currentPath === '/accept-invite';
          if (isPublicAuthPage) {
            console.log(`🔒 API: Already on ${currentPath} page, not attempting refresh`);
            return Promise.reject(error);
          }

          // Only log out for certain endpoints, not password updates
          const url = originalRequest?.url || '';
          const isPasswordUpdate = url.includes('/account/update-password');
          const isEmailUpdate = url.includes('/account/update-email');
          const isRefreshEndpoint = url.includes('/auth/refresh');
          const isLoginEndpoint = url.includes('/auth/login');

          // For password/email updates, let the component handle the 401 error
          if (isPasswordUpdate || isEmailUpdate) {
            console.log('🔒 API: 401 error on password/email endpoint, letting component handle it');
            return Promise.reject(error);
          }

          // Don't try to refresh if we're already logging in
          if (isLoginEndpoint) {
            console.log('🔒 API: Login endpoint failed, not attempting refresh');
            return Promise.reject(error);
          }

          // If refresh endpoint itself failed, redirect to login (unless on public auth page)
          if (isRefreshEndpoint) {
            console.log('🔒 API: Refresh token expired, logging out');
            this.isRefreshing = false;
            localStorage.clear();
            const currentPath = window.location.pathname;
            const isPublicAuthPage = currentPath === '/login' || currentPath === '/forgot-password' || currentPath === '/register' || currentPath === '/register/verify-email' || currentPath === '/accept-invite';
            if (!isPublicAuthPage) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          // If already refreshing, queue this request
          if (this.isRefreshing) {
            console.log('🔄 API: Already refreshing token, queuing request...');
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve,
                reject,
                config: originalRequest
              });
            });
          }

          // 🔒 Try to refresh the access token
          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            console.log('🔄 API: Access token expired, attempting refresh...');
            const refreshResponse = await this.api.post('/auth/refresh', {});
            console.log('✅ API: Token refreshed successfully, response:', refreshResponse.data);

            // Wait longer to ensure cookies are fully set and available
            // This is critical - cookies need time to be processed by the browser
            await new Promise(resolve => setTimeout(resolve, 200));

            // Verify CSRF token is available before retrying (as a proxy for cookies being set)
            let retries = 0;
            while (retries < 5) {
              const csrfToken = this.getCookie('csrf_token');
              if (csrfToken) {
                console.log('✅ API: CSRF token confirmed available after refresh');
                break;
              }
              console.log(`⏳ API: Waiting for CSRF token (attempt ${retries + 1}/5)...`);
              await new Promise(resolve => setTimeout(resolve, 100));
              retries++;
            }

            // Process queued requests
            console.log(`🔄 API: Processing ${this.failedQueue.length} queued requests`);
            const queue = [...this.failedQueue];
            this.failedQueue = [];

            // Retry original request with fresh cookies
            const originalPromise = this.api(originalRequest).finally(() => {
              // Only set isRefreshing to false after original request completes
              // Queued requests will complete independently
              this.isRefreshing = false;
            });

            // Retry all queued requests (these will complete independently)
            queue.forEach(({ resolve, reject, config }) => {
              this.api(config)
                .then(resolve)
                .catch(reject);
            });

            return originalPromise;
          } catch (refreshError) {
            console.log('❌ API: Token refresh failed, logging out');
            this.isRefreshing = false;

            // Reject all queued requests
            this.failedQueue.forEach(({ reject }) => {
              reject(refreshError);
            });
            this.failedQueue = [];

            localStorage.clear();
            const currentPath = window.location.pathname;
            // Don't redirect if already on a public auth page (login, register, forgot-password)
            const isPublicAuthPage = currentPath === '/login' || currentPath === '/forgot-password' || currentPath === '/register' || currentPath === '/register/verify-email' || currentPath === '/accept-invite';
            if (!isPublicAuthPage) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          const message = retryAfter
            ? `Too many requests. Please wait ${retryAfter} seconds before trying again.`
            : 'Too many requests. Please slow down and try again in a moment.';
          return Promise.reject(Object.assign(error, { _rateLimited: true, _rateLimitMessage: message }));
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic HTTP methods for direct API access
  async get(url: string, config?: any) {
    return await this.api.get(url, config);
  }

  async post(url: string, data?: any, config?: any) {
    return await this.api.post(url, data, config);
  }

  async put(url: string, data?: any, config?: any) {
    return await this.api.put(url, data, config);
  }

  async patch(url: string, data?: any, config?: any) {
    return await this.api.patch(url, data, config);
  }

  async delete(url: string, config?: any) {
    return await this.api.delete(url, config);
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<{
    message: string;
    token: string;
    device_id: string;
    device_name: string;
    session_type: string;
    user: User
  }> {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  /** Get or create a web browser hardware/device ID for verify-email (device registration). */
  getWebHardwareId(): string {
    const key = 'web_hardware_id';
    try {
      let id = sessionStorage.getItem(key);
      if (!id) {
        id = 'web-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
        sessionStorage.setItem(key, id);
      }
      return id;
    } catch {
      return 'web-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
    }
  }

  async resendVerificationOtp(email: string): Promise<{ message: string }> {
    const response = await this.api.post('/auth/resend-otp', { email });
    return response.data;
  }

  async verifyEmail(email: string, otp: string): Promise<{ message: string; user_id: string }> {
    const response = await this.api.post('/auth/verify-email', { email, otp }, {
      headers: {
        'X-Hardware-ID': this.getWebHardwareId(),
        'X-Device-Name': 'Web Browser',
        'X-Device-Type': 'web',
      },
    });
    return response.data;
  }

  /** Phase 3: Get invitation details by token (public, for accept-invite page). Includes password_policy when invitee will be a company user. For company invites, includes company and subscription. */
  async getInvitationByToken(token: string): Promise<{
    id: string;
    invitee_email: string;
    company_id: string | null;
    role: string;
    mode: string;
    intent?: string; // 'new_org' | 'new_user' | 'join_existing_user'
    expires_at: string;
    password_policy?: {
      min_length: number;
      require_uppercase?: boolean;
      require_lowercase?: boolean;
      require_numbers?: boolean;
      require_special_chars?: boolean;
      prohibit_easy_to_guess?: boolean;
      prohibit_sequential_numbers?: boolean;
      prohibit_repeated_characters?: boolean;
      prohibit_common_passwords?: boolean;
      prohibit_user_info?: boolean;
    };
    company?: { id: string; name: string };
    subscription?: {
      max_full_licenses?: number | null;
      reader_licenses_per_full?: number | null;
      licensed_until?: string | null;
      contract_id?: string | null;
    };
    subscription_note?: string;
  }> {
    const response = await this.api.get(`/invitations/accept/${encodeURIComponent(token)}`);
    return response.data;
  }

  /** Phase 3: Accept invitation – submit details; on success backend sets auth cookies and returns login JSON (no OTP step). */
  async acceptInvitation(token: string, password: string, name?: string): Promise<{
    message?: string;
    user?: { id: string; email: string; name: string; role: string; company_id?: string | null; mode: string };
    access_token?: string;
    refresh_token?: string;
    device_id?: string;
    csrf_token?: string;
  }> {
    const response = await this.api.post('/invitations/accept', { token, password, name: name || '' });
    return response.data;
  }

  /** Accept a join_existing_user invitation — connects an existing personal account to a company. No password needed. */
  async acceptJoinInvitation(token: string): Promise<{ ok: boolean }> {
    const response = await this.api.post('/invitations/accept-join', { token });
    return response.data;
  }

  /** Phase 3: Verify OTP after accept-invitation submit; on success sets auth cookies and returns user (logged in). */
  async verifyInviteOtp(token: string, otp: string): Promise<{
    message: string;
    user: { id: string; email: string; name: string; role: string; company_id: string | null; mode: string };
    access_token: string;
    refresh_token: string;
    device_id: string;
    csrf_token: string;
  }> {
    const response = await this.api.post('/invitations/accept/verify-otp', { token, otp });
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get('/auth/profile');
    return response.data.user;
  }

  async refreshToken(): Promise<{ access_token: string; refresh_token: string; token: string }> {
    // 🔒 Refresh tokens are now in httpOnly cookies, no need to send anything
    const response = await this.api.post('/auth/refresh', {});
    return response.data;
  }

  // WebAuthn endpoints
  async webauthnBeginRegistration(userId: string, email: string, name: string, displayName: string): Promise<{ options: any; session_id: string }> {
    const response = await this.api.post('/auth/webauthn/register/begin', {
      user_id: userId,
      email,
      name,
      display_name: displayName,
    });
    return response.data;
  }

  async webauthnFinishRegistration(sessionId: string, response: any): Promise<{ success: boolean; message: string }> {
    const response_data = await this.api.post('/auth/webauthn/register/finish', {
      session_id: sessionId,
      response,
    });
    return response_data.data;
  }

  async webauthnBeginAuthentication(userId?: string, email?: string): Promise<{ options: any; session_id: string }> {
    const payload: any = {};
    if (userId) {
      payload.user_id = userId;
    } else if (email) {
      payload.email = email;
    } else {
      throw new Error('Either userId or email must be provided');
    }
    const response = await this.api.post('/auth/webauthn/login/begin', payload);
    return response.data;
  }

  async webauthnListCredentials(): Promise<{ credentials: Array<{ id: string; aaguid: string; attestation_type: string; transports: string[]; created_at: string; last_used_at?: string }>; count: number }> {
    const response = await this.api.get('/auth/webauthn/credentials');
    return response.data;
  }

  async webauthnDeleteCredential(credentialId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/auth/webauthn/credentials/${credentialId}`);
    return response.data;
  }

  async webauthnFinishAuthentication(sessionId: string, response: any, purpose?: 'login' | 'challenge'): Promise<{
    success: boolean;
    message: string;
    user?: User;
    device_id?: string;
    device_name?: string;
    session_type?: string;
  }> {
    const payload: any = {
      session_id: sessionId,
      response,
    };
    if (purpose) {
      payload.purpose = purpose;
      console.log('webauthnFinishAuthentication: Sending purpose:', purpose, 'payload:', JSON.stringify(payload).substring(0, 200));
    } else {
      console.log('webauthnFinishAuthentication: No purpose provided, payload:', JSON.stringify(payload).substring(0, 200));
    }
    const response_data = await this.api.post('/auth/webauthn/login/finish', payload);
    return response_data.data;
  }

  // File endpoints
  async getFiles(filters?: {
    searchTerm?: string;
    emailSearch?: string;
    tags?: string[];
    assetType?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: string;
    /** When 'admin': sysadmin sees only company assets; when 'sysadmin' or absent: sysadmin sees all */
    viewMode?: 'user' | 'admin' | 'sysadmin';
  }): Promise<{ files: File[]; count: number }> {
    const params: any = {};

    if (filters) {
      if (filters.searchTerm) params.searchTerm = filters.searchTerm;
      if (filters.emailSearch) params.emailSearch = filters.emailSearch;
      if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');
      if (filters.assetType) params.assetType = filters.assetType;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.viewMode && (filters.viewMode === 'admin' || filters.viewMode === 'sysadmin')) params.view_mode = filters.viewMode;
    }

    const response = await this.api.get('/files', { params });
    return response.data;
  }

  async getSharedWithMeFiles(): Promise<{ files: SharedFile[]; count: number }> {
    const response = await this.api.get('/files/shared-with-me');
    return response.data;
  }

  async getSharedWithMeCount(): Promise<number> {
    const response = await this.api.get('/files/shared-with-me/count');
    return response.data?.count ?? 0;
  }

  async getFilesSharedByMeCount(): Promise<number> {
    const response = await this.api.get('/files/count', { params: { shared_by_me: '1' } });
    return response.data?.count ?? 0;
  }

  async getFileById(id: string): Promise<{ file: File; metadata: any; recipients: FileRecipient[]; recipient_count: number; owner_email?: string }> {
    const response = await this.api.get(`/files/${id}`);
    return response.data;
  }

  async getActivityLogs(resourceType: string, resourceId: string, limit: number = 100, offset: number = 0): Promise<any> {
    const response = await this.api.get(`/activity-logs/resource/${resourceType}/${resourceId}`, {
      params: { limit, offset }
    });
    return response.data;
  }

  async getUserActivityLogs(userId: string, limit: number = 100, offset: number = 0): Promise<any> {
    const response = await this.api.get(`/activity-logs/user/${userId}`, {
      params: { limit, offset }
    });
    return response.data;
  }

  async addRecipient(fileId: string, recipient: {
    type: string;
    value: string;
    times_accessible?: number;
    allowed_access_from?: string;
    allowed_access_to?: string;
    is_external?: boolean;
  }): Promise<FileRecipient> {
    const response = await this.api.post(`/files/${fileId}/recipients`, recipient);
    return response.data;
  }

  async updateRecipient(fileId: string, recipientId: string, updates: {
    times_accessible?: number | null;
    allowed_access_from?: string;
    allowed_access_to?: string;
  }): Promise<FileRecipient> {
    const response = await this.api.put(`/files/${fileId}/recipients/${recipientId}`, updates);
    return response.data;
  }

  async removeRecipient(fileId: string, recipientId: string): Promise<void> {
    await this.api.delete(`/files/${fileId}/recipients/${recipientId}`);
  }

  async confirmEmailInvitation(fileId: string, email: string): Promise<void> {
    console.log('📧 API: Confirming email invitation for file:', fileId, 'email:', email);
    const response = await this.api.post(`/files/${fileId}/recipients/confirm-email`, { email });
    console.log('✅ API: Email invitation confirmed:', response.data);
    return response.data;
  }

  // Search Methods
  async searchAssets(filters: any): Promise<{ assets: File[]; count: number }> {
    const response = await this.getFiles(filters);
    return { assets: response.files, count: response.count };
  }

  async searchNotifications(filters?: any): Promise<{ notifications: Notification[]; count: number }> {
    const params: any = {};
    if (filters) {
      if (filters.emailSearch && filters.emailSearch.trim()) {
        params.email = filters.emailSearch.trim();
      }
      if (filters.fileNameSearch && filters.fileNameSearch.trim()) {
        params.file_name = filters.fileNameSearch.trim();
      }
      if (filters.dateFrom) params.start_date = filters.dateFrom;
      if (filters.dateTo) params.end_date = filters.dateTo;
      if (filters.notificationTypes && filters.notificationTypes.length > 0) {
        params.types = filters.notificationTypes.join(',');
    }
      if (filters.status && filters.status.length > 0) {
        params.status = filters.status.join(',');
      }
    }
    console.log('🔍 API: searchNotifications called with params:', params);
    const response = await this.api.get('/notifications', { params });
    console.log('🔍 API: searchNotifications response:', {
      count: response.data.count,
      notificationsCount: response.data.notifications?.length
    });
    return {
      notifications: response.data.notifications || [],
      count: response.data.count || response.data.notifications?.length || 0
    };
  }

  // Policy endpoints
  async getPolicies(): Promise<Policy[]> {
    const response = await this.api.get('/policies');
    // Handle structured response with policies array
    return response.data.policies || response.data;
  }

  async getPolicyById(id: string): Promise<Policy> {
    console.log('🔍 API: Fetching policy by ID:', id);
    const response = await this.api.get(`/policies/${id}`);
    console.log('📋 API: Policy response:', response.data);
    console.log('👥 API: Recipient rules in response:', response.data.settings?.recipient_rules);
    return response.data;
  }

  async createPolicy(policy: CreatePolicyRequest): Promise<Policy> {
    const response = await this.api.post('/policies', policy);
    return response.data;
  }

  async updatePolicy(id: string, policy: Partial<Policy>): Promise<Policy> {
    const response = await this.api.put(`/policies/${id}`, policy);
    return response.data;
  }

  async deletePolicy(id: string): Promise<void> {
    await this.api.delete(`/policies/${id}`);
  }

  // Asset Permissions endpoints
  async getAssetPermissionsById(id: string): Promise<AssetPermissions> {
    console.log('🔍 API: Fetching asset permissions by ID:', id);
    const response = await this.api.get(`/asset-permissions/${id}`);
    console.log('📋 API: Asset permissions response:', response.data);
    return response.data;
  }

  async updateAssetPermissions(id: string, settings: PolicySettings): Promise<AssetPermissions> {
    console.log('🔧 API: Updating asset permissions:', id, 'with settings:', settings);
    const response = await this.api.put(`/asset-permissions/${id}`, { settings });
    console.log('✅ API: Asset permissions updated:', response.data);
    return response.data;
  }

  // Role-based policy access - use the main policies endpoint with middleware handling
  async getPoliciesByRole(): Promise<Policy[]> {
    const response = await this.api.get('/policies');
    return response.data.policies || response.data;
  }

  async getCompanyPolicies(): Promise<Policy[]> {
    const response = await this.api.get('/policies');
    return response.data.policies || response.data;
  }

  async getUserPolicies(): Promise<Policy[]> {
    const response = await this.api.get('/policies/user');
    return response.data.policies || response.data;
  }

  async getCompanies(): Promise<Company[]> {
    console.log('⚙️ API: Fetching companies');
    const response = await this.api.get('/companies');
    console.log('✅ API: Successfully fetched companies');
    const raw = response.data?.companies ?? response.data;
    return Array.isArray(raw) ? raw : [];
  }

  /** Companies that have at least one available full or reader license (sysadmin create-user form). */
  async getCompaniesWithAvailableLicenses(): Promise<CompanyWithLicenseAvailability[]> {
    const response = await this.api.get('/companies/with-available-licenses');
    const raw = response.data?.companies ?? response.data;
    return Array.isArray(raw) ? raw : [];
  }

  async getCompaniesCount(): Promise<{ total: number; active: number; inactive: number }> {
    const response = await this.api.get('/companies/count');
    return response.data;
  }

  /** Check if a domain is already used by another company (domain column or allowed_email_domains). excludeCompanyId = current company when editing. */
  async checkDomainTaken(domain: string, excludeCompanyId?: string): Promise<{ taken: boolean; company_name?: string }> {
    const params = new URLSearchParams({ domain: domain.trim().toLowerCase() });
    if (excludeCompanyId) params.set('exclude_company_id', excludeCompanyId);
    const response = await this.api.get(`/companies/check-domain?${params.toString()}`);
    return response.data;
  }

  /** Get single company (for allowed_email_domains etc.) */
  async getCompany(companyId: string): Promise<{ id: string; name: string; settings?: string | Record<string, unknown> }> {
    const response = await this.api.get(`/companies/${companyId}`);
    return response.data;
  }

  // Company Security Settings endpoints
  async getCompanySecuritySettings(companyId: string): Promise<any> {
    console.log('⚙️ API: Fetching company security settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}`);
    console.log('✅ API: Successfully fetched company security settings');
    // Parse settings from company response
    const company = response.data;
    if (company.settings) {
      const settings = typeof company.settings === 'string' ? JSON.parse(company.settings) : company.settings;
      return settings.security_settings || {};
    }
    return {};
  }

  async updateCompanySecuritySettings(companyId: string, settings: {
    session_timeout_value?: number;
    session_timeout_unit?: 'minutes' | 'hours' | 'days';
    require_password_rotation?: boolean;
    password_rotation_days?: number;
    quick_encrypt_policy_file?: string;
    quick_encrypt_policy_folder?: string;
    quick_encrypt_policy_override?: boolean;
    screen_watermark_settings?: WatermarkSettings;
    print_watermark_settings?: WatermarkSettings;
    override_screen_watermark_settings?: boolean;
    override_print_watermark_settings?: boolean;
  }, activateNow: boolean = false): Promise<any> {
    console.log('⚙️ API: Updating company security settings for company', companyId, 'activateNow:', activateNow);
    const url = `/companies/${companyId}/security-settings${activateNow ? '?activate_now=true' : ''}`;
    const response = await this.api.patch(url, settings);
    console.log('✅ API: Successfully updated company security settings');
    return response.data;
  }

  // Company Policy Settings endpoints
  async getCompanyPolicySettings(companyId: string): Promise<{
    file_permissions?: {
      convert_original: boolean;
      save_changes: boolean;
      copy_paste: boolean;
      screen_capture: boolean;
      screen_watermark: boolean;
      print: boolean;
      print_watermark: boolean;
      share: boolean;
    };
    folder_permissions?: {
      create: boolean;
      copy: boolean;
      add: boolean;
      move: boolean;
      share: boolean;
      delete: boolean;
    };
  }> {
    console.log('⚙️ API: Fetching company policy settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/policy-settings`);
    console.log('✅ API: Successfully fetched company policy settings');
    return response.data || {};
  }

  async updateCompanyPolicySettings(
    companyId: string,
    settings: {
      file_permissions?: {
        convert_original: boolean;
        save_changes: boolean;
        copy_paste: boolean;
        screen_capture: boolean;
        screen_watermark: boolean;
        print: boolean;
        print_watermark: boolean;
        share: boolean;
      };
      folder_permissions?: {
        create: boolean;
        copy: boolean;
        add: boolean;
        move: boolean;
        share: boolean;
        delete: boolean;
      };
    },
    applyToExisting: boolean = false
  ): Promise<any> {
    console.log('⚙️ API: Updating company policy settings for company', companyId, 'applyToExisting:', applyToExisting);
    const url = `/companies/${companyId}/policy-settings${applyToExisting ? '?apply_to_existing=true' : ''}`;
    const response = await this.api.patch(url, settings);
    console.log('✅ API: Successfully updated company policy settings');
    return response.data;
  }

  // Company Password Settings endpoints
  async getCompanyPasswordSettings(companyId: string): Promise<{
    min_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_numbers: boolean;
    require_special_chars: boolean;
    prohibit_easy_to_guess: boolean;
    prohibit_sequential_numbers: boolean;
    max_sequential_numbers: number;
    prohibit_repeated_characters: boolean;
    max_repeated_characters: number;
    prohibit_common_passwords: boolean;
    prohibit_user_info: boolean;
    require_password_rotation: boolean;
    password_rotation_days: number;
    password_expiration_notification_days: number;
  }> {
    console.log('⚙️ API: Fetching company password settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/password-settings`);
    console.log('✅ API: Successfully fetched company password settings');
    return response.data || {
      min_length: 8,
      require_uppercase: false,
      require_lowercase: false,
      require_numbers: false,
      require_special_chars: false,
      prohibit_easy_to_guess: false,
      prohibit_sequential_numbers: false,
      max_sequential_numbers: 3,
      prohibit_repeated_characters: false,
      max_repeated_characters: 3,
      prohibit_common_passwords: false,
      prohibit_user_info: false,
      require_password_rotation: false,
      password_rotation_days: 90,
      password_expiration_notification_days: 7,
    };
  }

  async updateCompanyPasswordSettings(
    companyId: string,
    settings: {
      min_length?: number;
      require_uppercase?: boolean;
      require_lowercase?: boolean;
      require_numbers?: boolean;
      require_special_chars?: boolean;
      prohibit_easy_to_guess?: boolean;
      prohibit_sequential_numbers?: boolean;
      max_sequential_numbers?: number;
      prohibit_repeated_characters?: boolean;
      max_repeated_characters?: number;
      prohibit_common_passwords?: boolean;
      prohibit_user_info?: boolean;
      require_password_rotation?: boolean;
      password_rotation_days?: number;
      password_expiration_notification_days?: number;
    }
  ): Promise<{
    message: string;
    settings: {
      min_length: number;
      require_uppercase: boolean;
      require_lowercase: boolean;
      require_numbers: boolean;
      require_special_chars: boolean;
      prohibit_easy_to_guess: boolean;
      prohibit_sequential_numbers: boolean;
      max_sequential_numbers: number;
      prohibit_repeated_characters: boolean;
      max_repeated_characters: number;
      prohibit_common_passwords: boolean;
      prohibit_user_info: boolean;
      require_password_rotation: boolean;
      password_rotation_days: number;
      password_expiration_notification_days: number;
    };
  }> {
    console.log('⚙️ API: Updating company password settings for company', companyId);
    const response = await this.api.patch(`/companies/${companyId}/password-settings`, settings);
    console.log('✅ API: Successfully updated company password settings');
    return response.data;
  }

  // Company Device Settings endpoints

  async getCompanyDeviceSettings(companyId: string): Promise<{
    allow_revoke_device: boolean;
    allow_create_cli_token: boolean;
  }> {
    console.log('⚙️ API: Fetching company device settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/device-settings`);
    console.log('✅ API: Successfully fetched company device settings');
    return response.data || { allow_revoke_device: true, allow_create_cli_token: true };
  }

  async getCompanyNotificationSettings(companyId: string): Promise<{
    send_invitation_email: { app: boolean; email: boolean };
    view_request_notification: { app: boolean; email: boolean };
    send_approval_email_to_viewer: { app: boolean; email: boolean };
    file_access_violation?: { app: boolean; email: boolean };
    file_shared?: { app: boolean; email: boolean };
    file_access_revoked?: { app: boolean; email: boolean };
    file_access_request?: { app: boolean; email: boolean };
    policy_updated?: { app: boolean; email: boolean };
    new_device_registered?: { app: boolean; email: boolean };
  }> {
    console.log('⚙️ API: Fetching company notification settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/notification-settings`);
    console.log('✅ API: Successfully fetched company notification settings');
    // Return with defaults if not present
    return response.data || {
      send_invitation_email: { app: false, email: true },
      view_request_notification: { app: true, email: true },
      send_approval_email_to_viewer: { app: true, email: true },
    };
  }

  async updateCompanyNotificationSettings(
    companyId: string,
    settings: {
      send_invitation_email?: { app: boolean; email: boolean };
      view_request_notification?: { app: boolean; email: boolean };
      send_approval_email_to_viewer?: { app: boolean; email: boolean };
      file_access_violation?: { app: boolean; email: boolean };
      file_shared?: { app: boolean; email: boolean };
      file_access_revoked?: { app: boolean; email: boolean };
      file_access_request?: { app: boolean; email: boolean };
      policy_updated?: { app: boolean; email: boolean };
      user_added_to_group?: { app: boolean; email: boolean };
      user_removed_from_group?: { app: boolean; email: boolean };
      new_device_registered?: { app: boolean; email: boolean };
    }
  ): Promise<void> {
    console.log('⚙️ API: Updating company notification settings for company', companyId);
    await this.api.patch(`/companies/${companyId}/notification-settings`, settings);
    console.log('✅ API: Successfully updated company notification settings');
  }

  // License Alert Settings endpoints
  async getCompanyLicenseAlertSettings(companyId: string): Promise<{
    app: boolean;
    email: boolean;
    thresholds?: {
      license_count?: {
        enabled: boolean;
        value: number;
        unit: 'count' | 'percentage';
      };
      expiry_time?: {
        enabled: boolean;
        value: number;
        unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
      };
    };
  }> {
    console.log('⚙️ API: Fetching license alert settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/license-alert-settings`);
    console.log('✅ API: Successfully fetched license alert settings');
    return response.data;
  }

  async updateCompanyLicenseAlertSettings(
    companyId: string,
    settings: {
      app: boolean;
      email: boolean;
      thresholds?: {
        license_count?: {
          enabled: boolean;
          value: number;
          unit: 'count' | 'percentage';
        };
        expiry_time?: {
          enabled: boolean;
          value: number;
          unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
        };
      };
    }
  ): Promise<void> {
    console.log('⚙️ API: Updating license alert settings for company', companyId);
    await this.api.patch(`/companies/${companyId}/license-alert-settings`, settings);
    console.log('✅ API: Successfully updated license alert settings');
  }

  async getSystemWideLicenseAlertSettings(): Promise<{
    app: boolean;
    email: boolean;
    thresholds?: {
      license_count?: {
        enabled: boolean;
        value: number;
        unit: 'count' | 'percentage';
      };
      expiry_time?: {
        enabled: boolean;
        value: number;
        unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
      };
    };
  }> {
    console.log('⚙️ API: Fetching system-wide license alert settings');
    const response = await this.api.get(`/sysadmin/license-alert-settings`);
    console.log('✅ API: Successfully fetched system-wide license alert settings');
    return response.data;
  }

  /** Sysadmin: list all subscriptions with license usage and expiry */
  async getSysadminSubscriptions(): Promise<{
    subscriptions: Array<{
      subscription: Record<string, unknown>;
      entity_name?: string;
      is_license_valid: boolean;
      available_full_licenses?: number;
      earliest_expiry?: string;
    }>;
  }> {
    const response = await this.api.get('/sysadmin/subscriptions');
    return response.data;
  }

  /** Get subscription for a company (company admin: own company; sysadmin: any). Used for license/device limits on Settings Company tab. */
  async getSubscriptionByCompany(companyId: string): Promise<{
    subscription: {
      id: string;
      entity_type: string;
      entity_id: string;
      plan_name?: string;
      status?: string;
      licensed_until?: string;
      max_full_licenses?: number;
      reader_licenses_per_full?: number | null;
      max_allowed_devices_per_user: number;
      max_folders_per_device: number;
      trial_ends_at?: string;
      [key: string]: unknown;
    };
    is_license_valid: boolean;
    available_full_licenses?: number;
  }> {
    const response = await this.api.get(`/subscriptions/company/${companyId}`);
    return response.data;
  }

  /** Update subscription for a company (sysadmin only). Used from company edit subscription tab. */
  async updateSubscriptionByCompany(companyId: string, body: {
    max_full_licenses?: number;
    reader_licenses_per_full?: number | null;
    max_auditor_licenses?: number | null;
    max_allowed_devices_per_user?: number;
    max_folders_per_device?: number;
    licensed_until?: string | null;
    cli_enabled?: boolean;
    cli_licensed_until?: string | null;
  }): Promise<{
    subscription: Record<string, unknown>;
    is_license_valid: boolean;
    available_full_licenses?: number;
  }> {
    const response = await this.api.patch(`/subscriptions/company/${companyId}`, body);
    return response.data;
  }

  /** Get subscription by ID (sysadmin or company admin for own company). Used for subscription detail page. */
  async getSubscriptionById(id: string): Promise<{
    subscription: Record<string, unknown>;
    is_license_valid: boolean;
    available_full_licenses?: number;
    entity_name?: string;
  }> {
    const response = await this.api.get(`/subscriptions/${id}`);
    return response.data;
  }

  /** Get licenses for a subscription (sysadmin or company admin for own company). */
  /** Optional: status=all|active|revoked, type=all|full|reader */
  async getLicensesBySubscriptionId(subscriptionId: string, params?: { status?: string; type?: string }): Promise<{ licenses: Array<{
    id: string;
    subscription_id: string;
    user_id: string;
    user_email?: string;
    company_id?: string;
    type: string;
    status: string;
    revoked_at?: string;
    created_at: string;
    updated_at: string;
  }> }> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.type) q.set('type', params.type);
    const query = q.toString();
    const url = query ? `/subscriptions/${subscriptionId}/licenses?${query}` : `/subscriptions/${subscriptionId}/licenses`;
    const response = await this.api.get(url);
    return response.data;
  }

  /** Sysadmin: platform metrics (total, expiring soon, low licenses) */
  async getSysadminMetrics(): Promise<{
    total_subscriptions: number;
    expiring_soon_count: number;
    expiring_soon_days: number;
    low_licenses_count: number;
  }> {
    const response = await this.api.get('/sysadmin/metrics');
    return response.data;
  }

  /** Sysadmin: get platform defaults (trial, reader invites, grace period). License alert lives in default_settings_company/personal. */
  async getPlatformDefaults(): Promise<{
    id: string;
    default_reader_licenses_per_full: number;
    default_reader_invites_personal: number;
    default_max_devices_per_user?: number;
    default_max_folders_per_user?: number;
    default_max_devices_personal?: number;
    default_max_folders_personal?: number;
    default_trial_days?: number;
    default_trial_full_licenses: number;
    default_max_auditor_licenses?: number;
    default_cli_enabled?: boolean;
    graceful_license_deactivation_days: number;
    // GOV-195: per-recipient-class trial-alert milestones.
    // Sign convention: negative=before expiry, 0=expiry day, positive=after.
    // Null/undefined = trial alerts disabled for that recipient class.
    trial_alert_offsets_days_company?: number[];
    trial_alert_offsets_days_personal?: number[];
    activated_at: string;
    created_at: string;
  }> {
    const response = await this.api.get('/sysadmin/platform-defaults');
    return response.data;
  }

  /** Sysadmin: update platform defaults (creates new version).
   *  trial_alert_offsets_days_{company,personal}:
   *    - undefined → preserve current
   *    - null      → UNSET (writes NULL to the column)
   *    - number[]  → overwrite with that array
   */
  async updatePlatformDefaults(updates: {
    default_reader_licenses_per_full?: number;
    default_reader_invites_personal?: number;
    default_max_devices_per_user?: number;
    default_max_folders_per_user?: number;
    default_max_devices_personal?: number;
    default_max_folders_personal?: number;
    default_trial_days?: number;
    default_trial_full_licenses?: number;
    default_max_auditor_licenses?: number;
    default_cli_enabled?: boolean;
    graceful_license_deactivation_days?: number;
    trial_alert_offsets_days_company?: number[] | null;
    trial_alert_offsets_days_personal?: number[] | null;
  }): Promise<Record<string, unknown>> {
    const response = await this.api.put('/sysadmin/platform-defaults', updates);
    return response.data;
  }

  // ───────────────────────── GOV-195: trial-alert system templates ─────────────────────────
  // Sysadmin-only. Editing surface for the 4 trial-alert template types
  // (pre/post × company/personal) × 6 locales = 24 system rows.

  /** GET ?type=...&locale=... → single row; type only → array of 6 locale rows;
   *  no filter → all (currently 24 trial-alert rows). */
  async getSystemEmailTemplates(filter?: { type?: string; locale?: string }): Promise<
    Array<{
      id: string;
      type: string;
      locale: string;
      status: string;
      subject: string;
      html_content: string;
      updated_at: string;
    }>
    | {
        id: string;
        type: string;
        locale: string;
        status: string;
        subject: string;
        html_content: string;
        updated_at: string;
      }
  > {
    const params = new URLSearchParams();
    if (filter?.type) params.set('type', filter.type);
    if (filter?.locale) params.set('locale', filter.locale);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/sysadmin/email-templates/system${qs}`);
    return response.data;
  }

  async updateSystemEmailTemplate(
    type: string,
    locale: string,
    body: { subject: string; html_content: string },
  ): Promise<{ id: string; type: string; locale: string; subject: string; html_content: string; updated_at: string }> {
    const response = await this.api.put(
      `/sysadmin/email-templates/system?type=${encodeURIComponent(type)}&locale=${encodeURIComponent(locale)}`,
      body,
    );
    return response.data;
  }

  async previewSystemEmailTemplate(payload: {
    type: string;
    locale: string;
    sample_vars: Record<string, unknown>;
  }): Promise<{ subject: string; html_content: string }> {
    const response = await this.api.post('/sysadmin/email-templates/system/preview', payload);
    return response.data;
  }

  /** Sysadmin: get default company settings (singleton; policy, password, security, watermark, notification, asset_protection, device_settings) */
  async getDefaultSettingsCompany(): Promise<{
    policy?: unknown;
    password?: unknown;
    security?: unknown;
    watermark?: unknown;
    notification?: unknown;
    asset_protection?: unknown;
    device_settings?: unknown;
  }> {
    const response = await this.api.get('/sysadmin/default-settings/company');
    return response.data;
  }

  /** Sysadmin: update default company settings (backend sets updated_at, updated_by) */
  async updateDefaultSettingsCompany(payload: {
    policy?: unknown;
    password?: unknown;
    security?: unknown;
    watermark?: unknown;
    notification?: unknown;
    asset_protection?: unknown;
    device_settings?: unknown;
  }): Promise<{ message: string }> {
    const response = await this.api.put('/sysadmin/default-settings/company', payload);
    return response.data;
  }

  /** Sysadmin: get default personal settings (singleton; policy, security, watermark, notification, asset_protection, device_settings) */
  async getDefaultSettingsPersonal(): Promise<{
    policy?: unknown;
    security?: unknown;
    watermark?: unknown;
    notification?: unknown;
    asset_protection?: unknown;
    device_settings?: unknown;
  }> {
    const response = await this.api.get('/sysadmin/default-settings/personal');
    return response.data;
  }

  /** Sysadmin: update default personal settings (backend sets updated_at, updated_by) */
  async updateDefaultSettingsPersonal(payload: {
    policy?: unknown;
    security?: unknown;
    watermark?: unknown;
    notification?: unknown;
    asset_protection?: unknown;
    device_settings?: unknown;
  }): Promise<{ message: string }> {
    const response = await this.api.put('/sysadmin/default-settings/personal', payload);
    return response.data;
  }

  /** Company usages: licenses (active+revoked), reader licenses, usage rows (admin: own company; sysadmin: any). Optional: status=all|active|revoked, type=all|full|reader */
  async getCompanyUsages(companyId: string, params?: { status?: string; type?: string }): Promise<CompanyUsagesResponse> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.type) q.set('type', params.type);
    const query = q.toString();
    const url = query ? `/companies/${companyId}/usages?${query}` : `/companies/${companyId}/usages`;
    const response = await this.api.get(url);
    return response.data;
  }

  /** Sysadmin: platform-wide usage overview (companies, licenses, usage rows, quotas) */
  async getSysadminUsagesOverview(): Promise<{
    total_companies: number;
    full_licenses_used: number;
    reader_licenses_used: number;
    total_usage_rows: number;
  }> {
    const response = await this.api.get('/sysadmin/usages/overview');
    return response.data;
  }

  /** Sysadmin: backfill usages from licenses and device/folder counts */
  async syncSysadminUsages(): Promise<{ synced: number; message: string }> {
    const response = await this.api.post('/sysadmin/usages/sync');
    return response.data;
  }

  /** Sysadmin: usage timeline (recent usage updates; period: 1h, 24h, 7d, 30d) */
  async getSysadminUsagesTimeline(period: string): Promise<{
    period: string;
    since: string;
    last_synced_at: string;
    entries: Array<{
      user_id: string;
      user_email: string;
      company_id: string;
      company_name: string;
      subscription_id: string;
      devices_count: number;
      folders_count: number;
    }>;
  }> {
    const response = await this.api.get(`/sysadmin/usages/timeline?period=${encodeURIComponent(period)}`);
    return response.data;
  }

  /** Sysadmin: personal usages (users without company) */
  async getSysadminUsagesPersonal(): Promise<{
    entity_type: string;
    usage_rows: Array<{
      user_id: string;
      user_email: string;
      devices_count: number;
      folders_count: number;
      invites_sent_full: number;
      invites_sent_reader: number;
    }>;
  }> {
    const response = await this.api.get('/sysadmin/usages/personal');
    return response.data;
  }

  /** Invitations: list by company (admin/sysadmin only). Optional inviter_id for invite history. */
  async getInvitationsByCompany(companyId: string, options?: { status?: string; inviter_id?: string }): Promise<{ invitations: Invitation[] }> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.inviter_id) params.set('inviter_id', options.inviter_id);
    const qs = params.toString();
    const url = qs ? `/companies/${companyId}/invitations?${qs}` : `/companies/${companyId}/invitations`;
    const response = await this.api.get(url);
    return response.data;
  }

  /** Invitations: list current user's invites (reader invites for regular users) */
  async getInvitationsMine(status?: string): Promise<{ invitations: Invitation[] }> {
    const url = status ? `/invitations/mine?status=${encodeURIComponent(status)}` : '/invitations/mine';
    const response = await this.api.get(url);
    return response.data;
  }

  /** Invitations: create (sends team_invitation email when backend mailer configured) */
  async createInvitation(companyId: string, body: { invitee_email: string; role?: string; mode?: string }): Promise<Record<string, unknown>> {
    const response = await this.api.post(`/companies/${companyId}/invitations`, body);
    return response.data;
  }

  /** Sysadmin: get organisation-admin-invite system template (for preview / edit). */
  async getOrganisationInviteTemplate(): Promise<{ id: string; subject: string; html_content: string; name?: string; description?: string }> {
    const response = await this.api.get('/companies/system/email-templates/organisation_admin_invite');
    return response.data;
  }

  /** Sysadmin: send admin invite email for a company (creates invitation and sends organisation-admin-invite email). Optional body: { subscription_note } to set the note text in the email. */
  async sendCompanyAdminInvite(companyId: string, body?: { subscription_note?: string }): Promise<Record<string, unknown>> {
    const response = await this.api.post(`/companies/${companyId}/send-admin-invite`, body ?? {});
    return response.data;
  }

  /** Invitations: cancel */
  async cancelInvitation(invitationId: string): Promise<void> {
    await this.api.delete(`/invitations/${invitationId}`);
  }

  /** Get invitation details by ID (company admin / sysadmin; for usage page link) */
  async getInvitationById(invitationId: string): Promise<{
    id: string;
    company_id: string;
    inviter_id: string;
    inviter_email: string;
    invitee_email: string;
    role: string;
    mode: string;
    status: string;
    created_at: string;
    expires_at: string;
    used_at: string | null;
    used_by_user_id: string | null;
  }> {
    const response = await this.api.get(`/invitations/${invitationId}`);
    return response.data;
  }

  /** Invitations: resend (extends expiry) */
  async resendInvitation(invitationId: string): Promise<Record<string, unknown>> {
    const response = await this.api.post(`/invitations/${invitationId}/resend`);
    return response.data;
  }

  /** Invitations: update role and mode (pending only; company admin) */
  async updateInvitation(invitationId: string, body: { role: string; mode: string }): Promise<Invitation> {
    const response = await this.api.patch(`/invitations/${invitationId}`, body);
    return response.data;
  }

  async updateSystemWideLicenseAlertSettings(settings: {
    app: boolean;
    email: boolean;
    thresholds?: {
      license_count?: {
        enabled: boolean;
        value: number;
        unit: 'count' | 'percentage';
      };
      expiry_time?: {
        enabled: boolean;
        value: number;
        unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
      };
    };
  }): Promise<void> {
    console.log('⚙️ API: Updating system-wide license alert settings');
    await this.api.patch(`/sysadmin/license-alert-settings`, settings);
    console.log('✅ API: Successfully updated system-wide license alert settings');
  }

  // Company Asset Protection Settings endpoints
  async getCompanyAssetProtectionSettings(companyId: string): Promise<{
    ownership_transfer_restrictions?: {
      enabled: boolean;
      allow_only_company_users: boolean;
      allowed_domains: string[];
    };
    recipient_restrictions?: {
      enabled: boolean;
      allow_only_company_users: boolean;
      allowed_domains: string[];
    };
    keys_backup_strategy?: {
      when_user_leaves?: {
        transfer_ownership_to_admin_on_user_leave: boolean;
        allow_key_backup_on_self_deletion: boolean;
      };
      allow_file_key_download: boolean;
      allow_multiple_file_key_download: boolean;
    };
  }> {
    console.log('⚙️ API: Fetching company asset protection settings for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/asset-protection-settings`);
    console.log('✅ API: Successfully fetched company asset protection settings');
    return response.data || {
      ownership_transfer_restrictions: {
        enabled: false,
        allow_only_company_users: false,
        allowed_domains: [],
      },
      recipient_restrictions: {
        enabled: false,
        allow_only_company_users: false,
        allowed_domains: [],
      },
      keys_backup_strategy: {
        when_user_leaves: {
          transfer_ownership_to_admin_on_user_leave: false,
          allow_key_backup_on_self_deletion: false,
        },
        allow_file_key_download: false,
        allow_multiple_file_key_download: false,
      },
    };
  }

  async updateCompanyAssetProtectionSettings(
    companyId: string,
    settings: {
      ownership_transfer_restrictions?: {
        enabled: boolean;
        allow_only_company_users: boolean;
        allowed_domains: string[];
      };
      recipient_restrictions?: {
        enabled: boolean;
        allow_only_company_users: boolean;
        allowed_domains: string[];
      };
    }
  ): Promise<{
    message: string;
    settings: {
      ownership_transfer_restrictions?: {
        enabled: boolean;
        allow_only_company_users: boolean;
        allowed_domains: string[];
      };
      recipient_restrictions?: {
        enabled: boolean;
        allow_only_company_users: boolean;
        allowed_domains: string[];
      };
    };
  }> {
    console.log('⚙️ API: Updating company asset protection settings for company', companyId);
    console.log('⚙️ API: Settings payload:', JSON.stringify(settings, null, 2));
    const response = await this.api.patch(`/companies/${companyId}/asset-protection-settings`, settings);
    console.log('✅ API: Successfully updated company asset protection settings');
    console.log('✅ API: Response:', JSON.stringify(response.data, null, 2));
    return response.data;
  }

  async updateCompanyDeviceSettings(
    companyId: string,
    settings: {
      allow_revoke_device: boolean;
      allow_create_cli_token: boolean;
    }
  ): Promise<{
    message: string;
    settings: {
      allow_revoke_device: boolean;
      allow_create_cli_token: boolean;
    };
  }> {
    console.log('⚙️ API: Updating company device settings for company', companyId);
    const response = await this.api.patch(`/companies/${companyId}/device-settings`, settings);
    console.log('✅ API: Successfully updated company device settings');
    return response.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await this.api.get(`/users/${id}`);
    return response.data;
  }

  async createUser(user: CreateUserRequest): Promise<User> {
    const response = await this.api.post('/users', user);
    return response.data;
  }

  async updateUser(id: string, user: UpdateUserRequest): Promise<User> {
    const response = await this.api.put(`/users/${id}`, user);
    return response.data;
  }

  /** Delete user. Uses longer timeout (60s); key cleanup runs in background on server. */
  async deleteUser(id: string): Promise<void> {
    await this.api.delete(`/users/${id}`, { timeout: 60000 });
  }

  /**
   * Delete user with SSE progress. Use when Accept: text/event-stream is desired.
   * Calls onPhase(phase) for each backend phase; resolves when phase is "done", rejects on "error" or non-2xx.
   */
  async deleteUserStreaming(id: string, onPhase: (phase: string) => void): Promise<void> {
    const baseURL = this.api.defaults.baseURL || '/api/v1';
    const url = `${baseURL}/users/${id}`;
    const csrfToken = this.getCookie('csrf_token');
    const headers: HeadersInit = {
      Accept: 'text/event-stream',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };
    const res = await fetch(url, { method: 'DELETE', credentials: 'include', headers });
    if (!res.ok) {
      const text = await res.text();
      let errMsg = text;
      try {
        const j = JSON.parse(text);
        if (j?.error) errMsg = j.error;
      } catch (_) {}
      throw new Error(errMsg || `Delete failed: ${res.status}`);
    }
    const reader = res.body?.getReader();
    if (!reader) throw new Error('No response body');
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const obj = JSON.parse(raw) as { phase?: string; message?: string };
          const phase = obj.phase;
          if (phase === 'error') {
            throw new Error(obj.message || 'Delete failed');
          }
          if (phase) {
            onPhase(phase);
            if (phase === 'done') return;
          }
        } catch (e) {
          if (e instanceof Error && e.message !== 'Delete failed' && !e.message.startsWith('Delete failed')) throw e;
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }
    if (buffer.trim() && buffer.startsWith('data: ')) {
      const raw = buffer.slice(6).trim();
      try {
        const obj = JSON.parse(raw) as { phase?: string; message?: string };
        if (obj.phase === 'error') throw new Error(obj.message || 'Delete failed');
        if (obj.phase) onPhase(obj.phase);
      } catch (e) {
        if (e instanceof Error && e.message !== 'Delete failed') throw e;
      }
    }
  }

  // Auto-encrypt folder endpoints
  async createAutoEncryptFolder(data: any, opts?: { deviceId?: string }): Promise<AutoEncryptFolder> {
    console.log('🆕 API: Creating auto-encrypt folder:', data, 'opts:', opts);
    const headers: any = {};
    if (opts?.deviceId) {
      headers['X-Device-ID'] = opts.deviceId;
    }
    const response = await this.api.post('/auto-encrypt-folders', data, { headers });
    return response.data;
  }
  // Org/company folder TEMPLATE creation (admins). This is the device-free
  // endpoint — the web only creates templates, never device-bound folders.
  async createAutoEncryptFolderTemplate(data: any): Promise<AutoEncryptFolder> {
    console.log('🆕 API: Creating auto-encrypt folder template:', data);
    const response = await this.api.post('/auto-encrypt-folders/templates', data);
    return response.data;
  }
  async getAutoEncryptFolders(): Promise<AutoEncryptFolder[]> {
    console.log('🔍 API: Fetching auto-encrypt folders');
    const response = await this.api.get('/auto-encrypt-folders');
    console.log('✅ API: Successfully fetched auto-encrypt folders:', response.data.count, 'folders');
    return response.data.folders;
  }

  async getAutoEncryptFolderById(id: string): Promise<AutoEncryptFolder> {
    console.log('🔍 API: Fetching auto-encrypt folder by ID:', id);
    const response = await this.api.get(`/auto-encrypt-folders/${id}`);
    console.log('✅ API: Successfully fetched auto-encrypt folder:', response.data);
    return response.data;
  }

  async updateAutoEncryptFolder(id: string, data: UpdateAutoEncryptFolderRequest): Promise<AutoEncryptFolder> {
    console.log('🔧 API: Updating auto-encrypt folder:', id, 'with data:', data);
    const response = await this.api.put(`/auto-encrypt-folders/${id}`, data);
    console.log('✅ API: Successfully updated auto-encrypt folder:', response.data);
    return response.data;
  }

  async deleteAutoEncryptFolder(id: string): Promise<void> {
    console.log('🗑️ API: Deleting auto-encrypt folder:', id);
    await this.api.delete(`/auto-encrypt-folders/${id}`);
    console.log('✅ API: Successfully deleted auto-encrypt folder:', id);
  }

  async delegateAutoEncryptFolder(id: string): Promise<void> {
    console.log('🔄 API: Delegating auto-encrypt folder:', id);
    await this.api.post(`/auto-encrypt-folders/${id}/delegate`);
    console.log('✅ API: Successfully delegated auto-encrypt folder:', id);
  }

  async deleteAutoEncryptFolderTemplate(id: string): Promise<void> {
    console.log('🗑️ API: Deleting auto-encrypt folder template:', id);
    await this.api.delete(`/auto-encrypt-folders/templates/${id}`);
    console.log('✅ API: Successfully deleted auto-encrypt folder template:', id);
  }

  async getAutoEncryptFolderCount(): Promise<number> {
    console.log('🔢 API: Fetching auto-encrypt folder count');
    const response = await this.api.get('/auto-encrypt-folders/count');
    console.log('✅ API: Successfully fetched auto-encrypt folder count:', response.data.count);
    return response.data.count;
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    console.log('📊 API: Fetching dashboard stats');
    // Use dedicated count endpoints for better performance
    const [filesCountResponse, foldersCountResponse] = await Promise.all([
      this.api.get('/files/count'),
      this.getAutoEncryptFolderCount().catch((error) => {
        console.warn('⚠️ API: Failed to fetch auto-encrypt folder count for dashboard:', error);
        return 0; // Don't fail dashboard if folders fail
      })
    ]);

    const stats: DashboardStats = {
      secured_assets: filesCountResponse.data.count,
      monitored_folders: foldersCountResponse,
      recent_notifications: [], // This would come from notifications endpoint
      service_status: {
        folder_monitoring: 'active' as const,
        google_drive_integration: 'inactive' as const,
        one_drive_integration: 'inactive' as const
      }
    };

    console.log('✅ API: Dashboard stats calculated:', {
      secured_assets: stats.secured_assets,
      monitored_folders: stats.monitored_folders
    });

    return stats;
  }

  // Device endpoints
  async getDevices(): Promise<any[]> {
    const response = await this.api.get('/devices');
    return response.data;
  }

  async getDesktopDevices(): Promise<any[]> {
    console.log('🖥️ API: Fetching desktop devices');
    const response = await this.api.get('/devices/desktop');
    console.log('✅ API: Successfully fetched desktop devices:', response.data.length, 'devices');
    return response.data;
  }

  async registerDevice(deviceData: any): Promise<any> {
    const response = await this.api.post('/devices', deviceData);
    return response.data;
  }

  async updateDevice(id: string, deviceData: any): Promise<any> {
    const response = await this.api.put(`/devices/${id}`, deviceData);
    return response.data;
  }

  async deleteDevice(id: string): Promise<void> {
    await this.api.delete(`/devices/${id}`);
  }

  // CLI token endpoints
  async createCLIToken(name: string, expiresAt?: string): Promise<{ id: string; token: string; name: string; status: string; expires_at?: string; created_at: string; message: string }> {
    const response = await this.api.post('/cli/tokens', { name, expires_at: expiresAt });
    return response.data;
  }

  async listCLITokens(): Promise<Array<{ id: string; name: string; status: string; expires_at?: string; created_at: string; last_used_at?: string }>> {
    const response = await this.api.get('/cli/tokens');
    return response.data.tokens || [];
  }

  async revokeCLIToken(tokenId: string): Promise<void> {
    await this.api.delete(`/cli/tokens/${tokenId}`);
  }

  // Session endpoints
  async getSessions(): Promise<any[]> {
    console.log('🔐 API: Fetching user sessions');
    const response = await this.api.get('/sessions');
    console.log('✅ API: Successfully fetched sessions:', response.data.sessions?.length || 0, 'sessions');
    return response.data.sessions || [];
  }

  async revokeSession(sessionId: string): Promise<void> {
    console.log('🚪 API: Revoking session:', sessionId);
    await this.api.delete(`/sessions/${sessionId}`);
    console.log('✅ API: Successfully revoked session:', sessionId);
  }

  async revokeAllOtherSessions(): Promise<void> {
    console.log('🚪 API: Revoking all other sessions');
    await this.api.delete('/sessions/all-others');
    console.log('✅ API: Successfully revoked all other sessions');
  }

  // User Preferences endpoints
  async getUserPreferences(): Promise<any> {
    console.log('⚙️ API: Fetching user preferences');
    const response = await this.api.get('/account/preferences');
    console.log('✅ API: Successfully fetched user preferences');
    return response.data;
  }

  async updateUserPreferences(preferences: any): Promise<any> {
    console.log('⚙️ API: Updating user preferences');
    const response = await this.api.put('/account/preferences', preferences);
    console.log('✅ API: Successfully updated user preferences');
    return response.data;
  }

  async updateUserPreferencesForUser(userId: string, preferences: any): Promise<any> {
    console.log('⚙️ API: Updating user preferences for user', userId);
    const response = await this.api.put(`/users/${userId}/preferences`, preferences);
    console.log('✅ API: Successfully updated user preferences for user', userId);
    return response.data;
  }

  async getUserPreferencesForUser(userId: string): Promise<any> {
    console.log('⚙️ API: Fetching user preferences for user', userId);
    const response = await this.api.get(`/users/${userId}/preferences`);
    console.log('✅ API: Successfully fetched user preferences for user', userId);
    return response.data;
  }

  async bulkUpdateUserPreferences(userIds: string[], ownershipTransferEnabled: boolean): Promise<{
    message: string;
    success_count: number;
    error_count: number;
    errors?: string[];
  }> {
    console.log('⚙️ API: Bulk updating user preferences for', userIds.length, 'users');
    const response = await this.api.post('/account/preferences/bulk-update', {
      user_ids: userIds,
      ownership_transfer_enabled: ownershipTransferEnabled,
    });
    console.log('✅ API: Successfully bulk updated user preferences');
    return response.data;
  }

  async getDomainSuggestions(companyId: string): Promise<{
    domains: string[];
  }> {
    console.log('⚙️ API: Fetching domain suggestions for company', companyId);
    const response = await this.api.get(`/companies/${companyId}/asset-protection-settings/domain-suggestions`);
    console.log('✅ API: Successfully fetched domain suggestions');
    return response.data;
  }


  // Account management endpoints
  async requestEmailUpdate(currentEmail: string, newEmail: string): Promise<{ success: boolean; message: string }> {
    console.log('📧 API: Requesting email update from', currentEmail, 'to', newEmail);
    try {
      const response = await this.api.post('/account/update-email', {
        current_email: currentEmail,
        new_email: newEmail,
      });
      console.log('✅ API: Successfully requested email update');
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Email update request failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      throw error;
    }
  }

  async confirmEmailUpdate(currentEmail: string, newEmail: string, otp: string): Promise<{ success: boolean; message: string }> {
    console.log('📧 API: Confirming email update with OTP');
    try {
      const response = await this.api.post('/account/update-email/confirmation', {
        current_email: currentEmail,
        new_email: newEmail,
        otp: otp,
      });
      console.log('✅ API: Successfully confirmed email update');
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Email update confirmation failed:', error.response?.data);
      throw error;
    }
  }

  async requestPasswordUpdate(email: string, currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    console.log('🔒 API: Requesting password update for', email);
    try {
      const response = await this.api.post('/account/update-password', {
        email: email,
        current_password: currentPassword,
        new_password: newPassword,
      });
      console.log('✅ API: Successfully requested password update');
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Password update request failed:', error.response?.data);
      throw error;
    }
  }

  async confirmPasswordUpdate(email: string, otp: string): Promise<{ success: boolean; message: string }> {
    console.log('🔒 API: Confirming password update with OTP');
    const response = await this.api.post('/account/update-password/confirmation', {
      email: email,
      otp: otp,
    });
    console.log('✅ API: Successfully confirmed password update');
    return response.data;
  }

  async getPasswordPolicy(email: string): Promise<{
    min_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_numbers: boolean;
    require_special_chars: boolean;
    prohibit_easy_to_guess: boolean;
    prohibit_sequential_numbers: boolean;
    max_sequential_numbers: number;
    prohibit_repeated_characters: boolean;
    max_repeated_characters: number;
    prohibit_common_passwords: boolean;
    prohibit_user_info: boolean;
    require_password_rotation: boolean;
    password_rotation_days: number;
    password_expiration_notification_days: number;
  }> {
    // Try protected endpoint first (for authenticated users)
    // Falls back to public endpoint if not authenticated (for registration flow)
    try {
      console.log('🔒 API: Fetching password policy from protected endpoint');
      const response = await this.api.get(`/account/password-policy`);
      console.log('✅ API: Successfully fetched password policy from protected endpoint');
      return response.data;
    } catch (error: any) {
      // If 401 (unauthorized), fall back to public endpoint (for registration)
      if (error.response?.status === 401) {
        console.log('🔒 API: Not authenticated, falling back to public endpoint for', email);
        const response = await this.api.get(`/auth/password-policy`, {
          params: { email }
        });
        console.log('✅ API: Successfully fetched password policy from public endpoint');
        return response.data;
      }
      // Re-throw other errors
      throw error;
    }
  }

  async updateName(name: string): Promise<{ message: string; user: { id: string; name: string; email: string } }> {
    console.log('👤 API: Updating user name to', name);
    try {
      const response = await this.api.put('/account/update-name', {
        name: name,
      });
      console.log('✅ API: Successfully updated name');
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Name update failed:', error.response?.data);
      throw error;
    }
  }

  /** Key backup for company users before self-deletion (when allowed). Triggers download of governate-key-backup.json */
  async getKeyExport(): Promise<void> {
    const response = await this.api.get('/account/key-export', { responseType: 'json' });
    const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'governate-key-backup.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Logging System API Methods
  async getAllLogs(params?: LogSearchQuery): Promise<LogSearchResult> {
    console.log('📊 API: Fetching all logs with params:', params);
    const response = await this.api.get('/logs', { params });
    console.log('✅ API: Successfully fetched logs:', response.data.total_count, 'total logs');
    return response.data;
  }

  async getFileLogs(fileId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching logs for file:', fileId);
    const response = await this.api.get(`/logs/files/${fileId}`, { params });
    console.log('✅ API: Successfully fetched file logs:', response.data.total, 'logs');
    return response.data;
  }

  async getUserLogs(userId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching logs for user:', userId);
    const response = await this.api.get(`/logs/users/${userId}`, { params });
    console.log('✅ API: Successfully fetched user logs:', response.data.total, 'logs');
    return response.data;
  }

  async searchLogs(query: LogSearchQuery): Promise<LogSearchResult> {
    console.log('🔍 API: Advanced log search with query:', query);
    const response = await this.api.post('/logs/search', query);
    console.log('✅ API: Search completed:', response.data.total, 'results');
    return response.data;
  }

  async getLogsByOperation(operation: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching logs by operation:', operation);
    const response = await this.api.get(`/logs/by-operation/${operation}`, { params });
    console.log('✅ API: Successfully fetched operation logs:', response.data.total, 'logs');
    return response.data;
  }

  async getLogsByDevice(deviceId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching logs by device:', deviceId);
    const response = await this.api.get(`/logs/by-device/${deviceId}`, { params });
    console.log('✅ API: Successfully fetched device logs:', response.data.total, 'logs');
    return response.data;
  }

  async getLogsByTimeRange(params: {
    start_date: string;
    end_date: string;
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching logs by time range:', params);
    const response = await this.api.get('/logs/by-time-range', { params });
    console.log('✅ API: Successfully fetched time range logs:', response.data.total, 'logs');
    return response.data;
  }

  async getViolationsByType(violationType: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching violations by type:', violationType);
    const response = await this.api.get(`/logs/violations/by-type/${violationType}`, { params });
    console.log('✅ API: Successfully fetched violation logs:', response.data.total, 'logs');
    return response.data;
  }

  async getCompanyLogs(companyId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching company logs:', companyId);
    const response = await this.api.get(`/logs/company/${companyId}`, { params });
    console.log('✅ API: Successfully fetched company logs:', response.data.total, 'logs');
    return response.data;
  }

  async getSystemLogs(params?: {
    limit?: number;
    offset?: number;
  }): Promise<LogSearchResult> {
    console.log('📊 API: Fetching system logs');
    const response = await this.api.get('/logs/system/all', { params });
    console.log('✅ API: Successfully fetched system logs:', response.data.total, 'logs');
    return response.data;
  }

  async getLogAnalytics(params?: {
    start_date?: string;
    end_date?: string;
    group_by?: string;
  }): Promise<AccessStatistics> {
    console.log('📈 API: Fetching log analytics with params:', params);
    const response = await this.api.get('/logs/analytics', { params });
    console.log('✅ API: Successfully fetched analytics');
    return response.data;
  }

  async getViolationReport(params?: {
    start_date?: string;
    end_date?: string;
    limit?: number;
  }): Promise<ViolationReport> {
    console.log('🚨 API: Fetching violation report with params:', params);
    const response = await this.api.get('/logs/violations', { params });
    console.log('✅ API: Successfully fetched violation report');
    return response.data;
  }


  async exportLogs(exportRequest: LogExportRequest): Promise<LogExportResponse> {
    console.log('📤 API: Exporting logs with request:', exportRequest);
    const response = await this.api.post('/logs/export', exportRequest);
    console.log('✅ API: Export job created:', response.data.export_id);
    return response.data;
  }

  async getExportStatus(exportId: string): Promise<LogExportResponse> {
    console.log('📤 API: Checking export status:', exportId);
    const response = await this.api.get(`/logs/export/${exportId}`);
    console.log('✅ API: Export status:', response.data.status);
    return response.data;
  }

  async downloadExport(exportId: string): Promise<Blob> {
    console.log('📥 API: Downloading export:', exportId);
    const response = await this.api.get(`/logs/export/${exportId}/download`, {
      responseType: 'blob'
    });
    console.log('✅ API: Export downloaded');
    return response.data;
  }

  async downloadExportDirect(exportRequest: LogExportRequest): Promise<Blob> {
    console.log('📥 API: Direct access-logs export (audit)');
    return this.downloadAccessLogsExport(exportRequest);
  }

  /** Export access logs from audit (BetterStack) as CSV, JSON, or PDF. Uses POST /audit/access-logs/export. */
  async downloadAccessLogsExport(exportRequest: LogExportRequest): Promise<Blob> {
    const body = {
      from: exportRequest.start_date ?? '',
      to: exportRequest.end_date ?? '',
      format: exportRequest.format
    };
    const response = await this.api.post('/audit/access-logs/export', body, {
      responseType: 'blob'
    });
    console.log('✅ API: Access logs export downloaded');
    return response.data;
  }

  // User Group Management
  async getUserGroups(): Promise<{ groups: UserGroup[] }> {
    console.log('👥 API: Fetching user groups');
    const response = await this.api.get('/user-groups');
    console.log('✅ API: Successfully fetched user groups');
    return response.data;
  }

  async createUserGroup(groupData: CreateUserGroupRequest): Promise<UserGroup> {
    console.log('👥 API: Creating user group:', groupData);
    const response = await this.api.post('/user-groups', groupData);
    console.log('✅ API: Successfully created user group');
    return response.data;
  }

  async updateUserGroup(id: string, groupData: UpdateUserGroupRequest): Promise<UserGroup> {
    console.log('👥 API: Updating user group:', id, groupData);
    const response = await this.api.put(`/user-groups/${id}`, groupData);
    console.log('✅ API: Successfully updated user group');
    return response.data;
  }

  async deleteUserGroup(id: string): Promise<void> {
    console.log('👥 API: Deleting user group:', id);
    await this.api.delete(`/user-groups/${id}`);
    console.log('✅ API: Successfully deleted user group');
  }

  async getUserGroupMembers(id: string): Promise<UserGroupMember[]> {
    console.log('👥 API: Fetching user group members:', id);
    const response = await this.api.get(`/user-groups/${id}/members`);
    console.log('✅ API: Successfully fetched user group members');
    return response.data.members;
  }

  async addUserGroupMembers(id: string, members: string[]): Promise<void> {
    console.log('👥 API: Adding user group members:', id, members);
    await this.api.post(`/user-groups/${id}/members`, { members });
    console.log('✅ API: Successfully added user group members');
  }

  async removeUserGroupMember(id: string, email: string): Promise<void> {
    console.log('👥 API: Removing user group member:', id, email);
    await this.api.delete(`/user-groups/${id}/members/${email}`);
    console.log('✅ API: Successfully removed user group member');
  }

  async importUserGroupMembers(id: string, csvData: string): Promise<void> {
    console.log('👥 API: Importing user group members from CSV:', id);
    await this.api.post(`/user-groups/${id}/import`, { csv_data: csvData });
    console.log('✅ API: Successfully imported user group members');
  }

  // Policy Recipient Management
  async getPolicyRecipients(policyId: string): Promise<PolicyRecipient[]> {
    console.log('📋 API: Fetching policy recipients:', policyId);
    const response = await this.api.get(`/policy-recipients/${policyId}`);
    console.log('✅ API: Successfully fetched policy recipients');
    return response.data.recipients;
  }

  async addPolicyRecipients(policyId: string, targets: RecipientTarget[]): Promise<void> {
    console.log('📋 API: Adding policy recipients:', policyId, targets);
    await this.api.post(`/policy-recipients/${policyId}`, { targets });
    console.log('✅ API: Successfully added policy recipients');
  }

  async replacePolicyRecipients(policyId: string, targets: RecipientTarget[]): Promise<void> {
    console.log('📋 API: Replacing policy recipients:', policyId, targets);
    await this.api.put(`/policy-recipients/${policyId}`, { targets });
    console.log('✅ API: Successfully replaced policy recipients');
  }

  async removePolicyRecipient(policyId: string, targetId: string): Promise<void> {
    console.log('📋 API: Removing policy recipient:', policyId, targetId);
    await this.api.delete(`/policy-recipients/${policyId}/${targetId}`);
    console.log('✅ API: Successfully removed policy recipient');
  }

  async clearPolicyRecipients(policyId: string): Promise<void> {
    console.log('📋 API: Clearing policy recipients:', policyId);
    await this.api.delete(`/policy-recipients/${policyId}`);
    console.log('✅ API: Successfully cleared policy recipients');
  }

  // Contact Management
  async getContacts(): Promise<Contact[]> {
    console.log('👤 API: Fetching contacts');
    const response = await this.api.get('/contacts');
    console.log('✅ API: Successfully fetched contacts');
    return response.data.contacts || [];
  }

  async createContact(contactData: CreateContactRequest): Promise<Contact> {
    console.log('👤 API: Creating contact:', contactData);
    const response = await this.api.post('/contacts', contactData);
    console.log('✅ API: Successfully created contact');
    return response.data.contact;
  }

  async updateContact(contactId: string, contactData: Partial<CreateContactRequest>): Promise<Contact> {
    console.log('👤 API: Updating contact:', contactId, contactData);
    const response = await this.api.put(`/contacts/${contactId}`, contactData);
    console.log('✅ API: Successfully updated contact');
    return response.data.contact;
  }

  async deleteContact(contactId: string): Promise<void> {
    console.log('👤 API: Deleting contact:', contactId);
    await this.api.delete(`/contacts/${contactId}`);
    console.log('✅ API: Successfully deleted contact');
  }


  async createBulkAssetPermissions(requests: Array<{ id: string, policy_id: string }>): Promise<any> {
    console.log('🔧 API: Creating bulk asset permissions:', requests);
    const response = await this.api.post('/asset-permissions/bulk', {
      files: requests
    });
    console.log('✅ API: Bulk asset permissions created:', response.data);
    return response.data;
  }

  async batchUpdateFiles(updates: Array<{ id: string, fields: any }>): Promise<any> {
    console.log('🔧 API: Batch updating files:', updates);
    const response = await this.api.patch('/files', {
      files: updates
    });
    console.log('✅ API: Files batch updated:', response.data);
    return response.data;
  }

  async changeOwner(fileIds: string[], newOwnerEmail: string): Promise<any> {
    console.log('🔧 API: Changing owner for files:', fileIds, 'to:', newOwnerEmail);
    const response = await this.api.post('/files/change-owner', {
      file_ids: fileIds,
      new_owner_email: newOwnerEmail
    });
    console.log('✅ API: Owner changed:', response.data);
    return response.data;
  }

  // Translation management methods
  async importTranslations(zipFile: globalThis.File, langId?: string, channel?: string): Promise<any> {
    console.log('🌍 API: Importing translations from ZIP file:', zipFile.name, langId ? `(language: ${langId})` : '(multi-language)', channel ? `(channel: ${channel})` : '');
    const formData = new FormData();
    formData.append('file', zipFile);
    
    // Use native fetch API to bypass axios Content-Type issues with FormData
    const baseURL = this.api.defaults.baseURL || '/api/v1';
    const params = new URLSearchParams();
    if (langId) params.append('langId', langId);
    if (channel) params.append('channel', channel);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const url = `${baseURL}/admin/translations/import${queryString}`;
    
    const csrfToken = this.getCookie('csrf_token');
    
    console.log('📤 Fetch request URL:', url);
    console.log('📤 FormData entries:', Array.from(formData.entries()).map(([k, v]) => [k, v instanceof File ? v.name : v]));
    
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'include', // Include cookies for auth
      headers: {
        'X-CSRF-Token': csrfToken || '',
        // Explicitly DO NOT set Content-Type - browser will set multipart/form-data with boundary
      },
    });
    
    console.log('📥 Response status:', response.status);
    console.log('📥 Response Content-Type:', response.headers.get('Content-Type'));
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `Request failed with status ${response.status}` }));
      throw new Error(errorData.error || `Request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ API: Translations imported:', data);
    return data;
  }

  async exportAllLanguages(channel?: string, format?: string): Promise<Blob> {
    console.log('📤 API: Exporting all languages', channel ? `(channel: ${channel})` : '', format ? `(format: ${format})` : '');
    const params = new URLSearchParams();
    if (channel) params.append('channel', channel);
    if (format) params.append('format', format);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/admin/translations/export/all${queryString}`, {
      responseType: 'blob',
    });
    console.log('✅ API: All languages exported');
    return response.data;
  }

  async exportLanguage(langId: string, channel?: string, format?: string): Promise<Blob> {
    console.log('📤 API: Exporting language:', langId, channel ? `(channel: ${channel})` : '', format ? `(format: ${format})` : '');
    const params = new URLSearchParams();
    if (channel) params.append('channel', channel);
    if (format) params.append('format', format);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/admin/translations/export/${langId}${queryString}`, {
      responseType: 'blob',
    });
    console.log('✅ API: Language exported');
    return response.data;
  }

  async exportNamespace(langId: string, namespace: string, channel?: string, format?: string): Promise<Blob> {
    console.log('📤 API: Exporting namespace:', langId, namespace, channel ? `(channel: ${channel})` : '', format ? `(format: ${format})` : '');
    const params = new URLSearchParams();
    if (channel) params.append('channel', channel);
    if (format) params.append('format', format);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/admin/translations/export/${langId}/${namespace}${queryString}`, {
      responseType: 'blob',
    });
    console.log('✅ API: Namespace exported');
    return response.data;
  }

  async updateTranslation(langId: string, namespace: string, content: any, channel?: string): Promise<any> {
    console.log('💾 API: Updating translation:', langId, namespace, channel ? `(channel: ${channel})` : '');
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    const response = await this.api.put(`/admin/translations/${langId}/${namespace}${params}`, {
      content,
      status: 'active',
      channel: channel || 'web', // Default to 'web' if not provided
    });
    console.log('✅ API: Translation updated:', response.data);
    return response.data;
  }

  async getTranslation(langId: string, namespace: string, channel?: string): Promise<any> {
    console.log('📥 API: Getting translation:', langId, namespace, channel ? `(channel: ${channel})` : '');
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    const response = await this.api.get(`/admin/translations/${langId}/${namespace}${params}`);
    console.log('✅ API: Translation retrieved');
    return response.data;
  }

  // Bulk recipient management methods
  async bulkAddRecipients(fileIds: string[], recipients: Array<{ type: string, value: string, times_accessible?: number, is_external: boolean }>): Promise<any> {
    console.log('🔧 API: Bulk adding recipients:', fileIds, recipients);
    const response = await this.api.post('/files/recipients/bulk', {
      file_ids: fileIds,
      recipients: recipients
    });
    console.log('✅ API: Bulk recipients added:', response.data);
    return response.data;
  }

  async bulkRemoveRecipients(fileIds: string[], recipientEmail: string): Promise<any> {
    console.log('🔧 API: Bulk removing recipients:', fileIds, recipientEmail);
    const response = await this.api.delete('/files/recipients/bulk', {
      data: {
        file_ids: fileIds,
        recipient_email: recipientEmail
      }
    });
    console.log('✅ API: Bulk recipients removed:', response.data);
    return response.data;
  }

  async bulkConfirmEmailInvitation(fileIds: string[], emails: string[]): Promise<any> {
    console.log('🔧 API: Bulk confirming email invitations:', fileIds, emails);
    const response = await this.api.post('/files/recipients/bulk/confirm-email', {
      file_ids: fileIds,
      emails: emails
    });
    console.log('✅ API: Bulk email confirmations sent:', response.data);
    return response.data;
  }

  // File blocking/unblocking methods
  async updateFileStatus(fileId: string, status: 'ready' | 'blocked'): Promise<any> {
    console.log('🔧 API: Updating file status:', fileId, 'to:', status);
    const response = await this.api.put(`/files/${fileId}/status`, { status });
    console.log('✅ API: File status updated:', response.data);
    return response.data;
  }

  async bulkUpdateFileStatus(fileIds: string[], status: 'ready' | 'blocked'): Promise<any> {
    console.log('🔧 API: Bulk updating file status for', fileIds.length, 'files to:', status);
    const response = await this.api.put('/files/bulk/status', {
      file_ids: fileIds,
      status: status
    });
    console.log('✅ API: Bulk file status updated:', response.data);
    return response.data;
  }

  // File deletion methods
  async deleteFile(fileId: string): Promise<any> {
    console.log('🔧 API: Deleting single file:', fileId);
    const response = await this.api.delete(`/files/${fileId}`);
    console.log('✅ API: File deleted:', response.data);
    return response.data;
  }

  async bulkDeleteFiles(fileIds: string[]): Promise<any> {
    console.log('🔧 API: Bulk deleting', fileIds.length, 'files');
    const response = await this.api.delete('/files/bulk', {
      data: {
        file_ids: fileIds
      }
    });
    console.log('✅ API: Bulk files deleted:', response.data);
    return response.data;
  }

  /** Export a single file key as .txt (filename = file_id.txt). Mode: 'encapsulated' | 'raw'. userId optional for company admin. */
  async exportFileKey(mode: 'encapsulated' | 'raw', fileId: string, userId?: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.post('/files/export', { mode, file_id: fileId, user_id: userId || undefined }, { responseType: 'blob' });
    const filename = response.headers['content-disposition']?.match(/filename=(.+)/)?.[1]?.trim().replace(/^["']|["']$/g, '') || `${fileId}.txt`;
    return { blob: response.data, filename };
  }

  /** Export multiple file keys as .csv (file_id, user_id, key). Mode: 'encapsulated' | 'raw'. userId optional for company admin. */
  async exportFileKeysBulk(mode: 'encapsulated' | 'raw', fileIds: string[], userId?: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.post('/files/export/bulk', { mode, file_ids: fileIds, user_id: userId || undefined }, { responseType: 'blob' });
    const filename = response.headers['content-disposition']?.match(/filename=(.+)/)?.[1]?.trim().replace(/^["']|["']$/g, '') || 'file_keys_export.csv';
    return { blob: response.data, filename };
  }

  // Access Request methods
  // Note: Recipients cannot specify requested values - only the file owner sets them when responding
  async createAccessRequest(fileId: string, requestType: 'access_count_limit' | 'access_period_limit' | 'asset_permissions', reason?: string): Promise<any> {
    console.log('🔧 API: Creating access request:', { fileId, requestType });
    const payload: any = {
      file_id: fileId,
      request_type: requestType,
    };
    if (reason) payload.reason = reason;

    const response = await this.api.post('/access-requests', payload);
    console.log('✅ API: Access request created:', response.data);
    return response.data;
  }

  async getPendingAccessRequests(): Promise<{ requests: any[] }> {
    console.log('🔧 API: Fetching pending access requests');
    const response = await this.api.get('/access-requests/pending');
    console.log('✅ API: Pending access requests fetched:', response.data);
    return response.data;
  }

  async getAllReceivedAccessRequests(): Promise<{ requests: any[] }> {
    console.log('🔧 API: Fetching all received access requests');
    const response = await this.api.get('/access-requests/received');
    console.log('✅ API: All received access requests fetched:', response.data);
    return response.data;
  }

  async getMyAccessRequests(): Promise<{ requests: any[] }> {
    console.log('🔧 API: Fetching my access requests');
    const response = await this.api.get('/access-requests/my-requests');
    console.log('✅ API: My access requests fetched:', response.data);
    return response.data;
  }

  async getAccessRequest(id: string): Promise<any> {
    console.log('🔧 API: Fetching access request:', id);
    const response = await this.api.get(`/access-requests/${id}`);
    console.log('✅ API: Access request fetched:', response.data);
    return response.data;
  }

  async getBulkAccessRequests(ids: string[]): Promise<any[]> {
    console.log('🔧 API: Fetching bulk access requests:', ids);
    if (ids.length === 0) {
      return [];
    }
    const response = await this.api.post(`/access-requests/bulk`, { ids });
    console.log('✅ API: Bulk access requests fetched:', response.data);
    return response.data.requests || [];
  }

  async respondToAccessRequest(id: string, status: 'accepted' | 'rejected', responseMessage?: string, timesAccessible?: number | null, allowedAccessTo?: string | null): Promise<any> {
    console.log('🔧 API: Responding to access request:', { id, status, responseMessage, timesAccessible, allowedAccessTo });
    const payload: any = { status };
    if (responseMessage) payload.response_message = responseMessage;
    // Only include times_accessible if explicitly provided (for access_count_limit requests)
    if (timesAccessible !== undefined) {
      if (timesAccessible !== null) {
        payload.times_accessible = timesAccessible;
      } else {
        payload.times_accessible = null;
      }
    }
    // Only include allowed_access_to if provided (for access_period_limit requests)
    // null means unlimited, undefined means use requested value
    if (allowedAccessTo !== undefined) {
      if (allowedAccessTo !== null) {
        payload.allowed_access_to = allowedAccessTo;
      } else {
        payload.allowed_access_to = null;
      }
    }

    const response = await this.api.post(`/access-requests/${id}/respond`, payload);
    console.log('✅ API: Access request responded:', response.data);
    return response.data;
  }

  async cancelAccessRequest(id: string): Promise<any> {
    console.log('🔧 API: Canceling access request:', id);
    const response = await this.api.post(`/access-requests/${id}/cancel`);
    console.log('✅ API: Access request canceled:', response.data);
    return response.data;
  }

  // License Request methods removed - license requests system has been removed

  // 🔒 Helper method to get cookie value by name
  private getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // User Management methods
  async getUsers(filters?: UserFilters): Promise<UsersResponse> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.company_id) params.append('company_id', filters.company_id);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await this.api.get(`/users?${params.toString()}`);
    return response.data;
  }

  // ============================================================================
  // Tag Management Methods
  // ============================================================================

  // Get all unique tags
  async getAllTags(): Promise<{ tags: string[]; count: number }> {
    console.log('🏷️ API: Fetching all tags');
    const response = await this.api.get('/tags/all');
    console.log('✅ API: Tags fetched:', response.data);
    return response.data;
  }

  // Search tags (autocomplete)
  async searchTags(query: string, limit: number = 20): Promise<{ tags: string[]; query: string; count: number }> {
    console.log('🔍 API: Searching tags:', query);
    const response = await this.api.get('/tags/search', {
      params: { q: query, limit }
    });
    console.log('✅ API: Tag search results:', response.data);
    return response.data;
  }

  // Get popular tags
  async getPopularTags(limit: number = 10): Promise<{ tags: PopularTag[]; count: number }> {
    console.log('📊 API: Fetching popular tags');
    const response = await this.api.get('/tags/popular', {
      params: { limit }
    });
    console.log('✅ API: Popular tags fetched:', response.data);
    return response.data;
  }

  // Get asset tags
  async getAssetTags(fileId: string): Promise<{ tags: AssetTag[]; count: number; asset_id: string }> {
    console.log('🏷️ API: Fetching tags for asset:', fileId);
    const response = await this.api.get(`/tags/assets/${fileId}`);
    console.log('✅ API: Asset tags fetched:', response.data);
    return response.data;
  }

  // Add tag to asset
  async addAssetTag(fileId: string, tagData: AddTagRequest): Promise<{ message: string; asset_id: string; tag_name: string; tag_type: string }> {
    console.log('➕ API: Adding tag to asset:', fileId, tagData);
    const response = await this.api.post(`/tags/assets/${fileId}`, tagData);
    console.log('✅ API: Tag added to asset:', response.data);
    return response.data;
  }

  // Remove tag from asset
  async removeAssetTag(fileId: string, tagName: string): Promise<{ message: string; asset_id: string; tag_name: string }> {
    console.log('➖ API: Removing tag from asset:', fileId, tagName);
    const response = await this.api.delete(`/tags/assets/${fileId}/${encodeURIComponent(tagName)}`);
    console.log('✅ API: Tag removed from asset:', response.data);
    return response.data;
  }

  // Bulk add tags to assets
  async bulkAddAssetTags(fileIds: string[], tagNames: string[], tagType: 'system' | 'company' | 'user' = 'user'): Promise<any> {
    console.log('➕ API: Bulk adding tags to assets:', fileIds, tagNames);
    const response = await this.api.post('/tags/assets/bulk/add', {
      file_ids: fileIds,
      tag_names: tagNames,
      tag_type: tagType
    });
    console.log('✅ API: Bulk tags added:', response.data);
    return response.data;
  }

  // Bulk remove tags from assets
  async bulkRemoveAssetTags(fileIds: string[], tagNames: string[]): Promise<any> {
    console.log('➖ API: Bulk removing tags from assets:', fileIds, tagNames);
    const response = await this.api.post('/tags/assets/bulk/remove', {
      file_ids: fileIds,
      tag_names: tagNames
    });
    console.log('✅ API: Bulk tags removed:', response.data);
    return response.data;
  }

  // Get policy tags
  async getPolicyTags(policyId: string): Promise<{ tags: PolicyTag[]; count: number; policy_id: string }> {
    console.log('🏷️ API: Fetching tags for policy:', policyId);
    const response = await this.api.get(`/tags/policies/${policyId}`);
    console.log('✅ API: Policy tags fetched:', response.data);
    return response.data;
  }

  // Add tag to policy
  async addPolicyTag(policyId: string, tagData: AddTagRequest): Promise<{ message: string; policy_id: string; tag_name: string; tag_type: string }> {
    console.log('➕ API: Adding tag to policy:', policyId, tagData);
    const response = await this.api.post(`/tags/policies/${policyId}`, tagData);
    console.log('✅ API: Tag added to policy:', response.data);
    return response.data;
  }

  // Remove tag from policy
  async removePolicyTag(policyId: string, tagName: string): Promise<{ message: string; policy_id: string; tag_name: string }> {
    console.log('➖ API: Removing tag from policy:', policyId, tagName);
    const response = await this.api.delete(`/tags/policies/${policyId}/${encodeURIComponent(tagName)}`);
    console.log('✅ API: Tag removed from policy:', response.data);
    return response.data;
  }

  // Notification methods
  async getNotifications(unreadOnly: boolean = false, limit?: number, offset?: number): Promise<{ notifications: any[]; total: number }> {
    console.log('📬 API: Fetching notifications:', { unreadOnly, limit, offset });
    const params: any = {
      unread_only: unreadOnly
    };
    // Only include limit and offset if explicitly provided
    if (limit !== undefined && limit !== null) {
      params.limit = limit;
    }
    if (offset !== undefined && offset !== null) {
      params.offset = offset;
    }
    const response = await this.api.get('/notifications', { params });
    console.log('✅ API: Notifications fetched:', response.data);
    return response.data;
  }

  async getUnreadNotificationCount(): Promise<{ unread_count: number }> {
    console.log('📊 API: Fetching unread notification count');
    const response = await this.api.get('/notifications/unread-count');
    console.log('✅ API: Unread count:', response.data);
    return response.data;
  }

  async markNotificationAsRead(notificationId: string): Promise<{ success: boolean; message: string }> {
    console.log('✅ API: Marking notification as read:', notificationId);
    const response = await this.api.put(`/notifications/${notificationId}/read`);
    console.log('✅ API: Notification marked as read:', response.data);
    return response.data;
  }

  async markAllNotificationsAsRead(): Promise<{ success: boolean; message: string }> {
    console.log('✅ API: Marking all notifications as read');
    const response = await this.api.put('/notifications/read-all');
    console.log('✅ API: All notifications marked as read:', response.data);
    return response.data;
  }

  async deleteNotification(notificationId: string): Promise<{ success: boolean; message: string }> {
    console.log('🗑️ API: Deleting notification:', notificationId);
    const response = await this.api.delete(`/notifications/${notificationId}`);
    console.log('✅ API: Notification deleted:', response.data);
    return response.data;
  }

  // Company Resources API Methods
  async getCompanyResources(companyId: string): Promise<CompanyResource> {
    console.log('📊 API: Getting company resources for:', companyId);
    const response = await this.api.get(`/company-resources/${companyId}`);
    console.log('✅ API: Company resources retrieved:', response.data);
    return response.data;
  }

  async getAvailableRegions(): Promise<{ regions: string[] }> {
    console.log('🌍 API: Getting available GCP regions');
    const response = await this.api.get('/company-resources/regions');
    console.log('✅ API: Available regions:', response.data);
    return response.data;
  }

  async checkSlugAvailability(slug: string, companyId?: string): Promise<{ available: boolean; slug: string }> {
    console.log('🔍 API: Checking slug availability:', slug);
    const params = new URLSearchParams({ slug });
    if (companyId) {
      params.append('companyId', companyId);
    }
    const response = await this.api.get(`/company-resources/slug/check?${params.toString()}`);
    console.log('✅ API: Slug availability:', response.data);
    return response.data;
  }

  async provisionInfisical(companyId: string, request: ProvisionInfisicalRequest): Promise<InfisicalInstance> {
    console.log('🚀 API: Provisioning Infisical for company:', companyId);
    const response = await this.api.post(`/company-resources/${companyId}/infisical/provision`, request);
    console.log('✅ API: Infisical provisioned:', response.data);
    return response.data;
  }

  async testTenantDatabase(companyId: string, request: TenantDatabaseRequest): Promise<{ ok: boolean; message?: string }> {
    const response = await this.api.post(`/company-resources/${companyId}/database/test`, request);
    return response.data;
  }

  async setTenantDatabase(companyId: string, request: TenantDatabaseRequest): Promise<{ ok: boolean; database_mode: string }> {
    const response = await this.api.patch(`/company-resources/${companyId}/database`, request);
    return response.data;
  }

  async clearTenantDatabase(companyId: string): Promise<{ ok: boolean; database_mode: string }> {
    const response = await this.api.delete(`/company-resources/${companyId}/database`);
    return response.data;
  }

  /** Download tenant database backup (pg_dump). Returns blob and suggested filename. */
  async getTenantDatabaseBackup(companyId: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this.api.get(`/company-resources/${companyId}/database/backup`, {
      responseType: 'blob',
    });
    const filename =
      response.headers['content-disposition']?.match(/filename=(.+)/)?.[1]?.trim().replace(/^["']|["']$/g, '') ||
      `backup-${companyId}-${new Date().toISOString().slice(0, 10)}.sql`;
    return { blob: response.data, filename };
  }

  /** Run reset.sql then init.sql on the tenant database (clean schema). */
  async resetTenantDatabase(companyId: string): Promise<{ ok: boolean; message: string }> {
    const response = await this.api.post(`/company-resources/${companyId}/database/reset`);
    return response.data;
  }

  /** Sysadmin only: set company resources flags (at least one of the four booleans). */
  async patchCompanyResource(companyId: string, payload: {
    allow_company_admins_resources?: boolean;
    allow_company_admins_spin_up_db?: boolean;
    allow_company_admins_bring_own_db?: boolean;
    allow_company_admins_backup_db?: boolean;
  }): Promise<{ message: string }> {
    const response = await this.api.patch(`/company-resources/${companyId}`, payload);
    return response.data;
  }

  // ─── Audit Logs ──────────────────────────────────────────────────────────────

  /** Fetch company-scoped audit events. Allowed for: admin, company_auditor. */
  async getAuditEvents(params: AuditQueryParams): Promise<AuditEvent[]> {
    const qs = buildAuditQueryString(params);
    const response = await this.api.get(`/audit/events${qs}`);
    return response.data ?? [];
  }

  /** Fetch system-scoped audit events. Allowed for: sysadmin, system_auditor. */
  async getSystemAuditEvents(params: AuditQueryParams): Promise<AuditEvent[]> {
    const qs = buildAuditQueryString(params);
    const response = await this.api.get(`/audit/system/events${qs}`);
    return response.data ?? [];
  }

  /** Export audit events (filtered results) as CSV, JSON, or PDF. POST /audit/events/export. */
  async exportAuditEvents(req: AuditExportRequest): Promise<Blob> {
    const response = await this.api.post('/audit/events/export', req, { responseType: 'blob' });
    return response.data;
  }

}

// User Group Types
export interface UserGroup {
  id: string;
  name: string;
  description?: string;
  member_count: number;
  created_at: string;
  created_by: string;
}

export interface UserGroupMember {
  id: string;
  group_id: string;
  contact_id: string;
  created_at: string;
}

export interface CreateUserGroupRequest {
  name: string;
  description?: string;
}

export interface UpdateUserGroupRequest {
  name?: string;
  description?: string;
}

// Policy Recipient Types
export interface PolicyRecipient {
  id: string;
  policy_id: string;
  user_id?: string;
  user_email?: string;
  company_id?: string;
  user_group_id?: string;
  created_at: string;
  created_by: string;
}

export interface RecipientTarget {
  type: 'user' | 'company' | 'user_group';
  user_id?: string;
  company_id?: string;
  user_group_id?: string;
}

// User Management Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'sysadmin' | 'admin' | 'user';
  company_id?: string;
  company_name?: string;
  is_active: boolean;
  is_verified: boolean;
  is_onboarded?: boolean;
  mode?: string; // "full" or "reader"
  last_login?: string;
  created_at: string;
  updated_at?: string;
  password_last_updated_at?: string; // When password was last changed
  file_count?: number; // Count of files with status 'ready' or 'blocked'
  /** When false for an inactive company user: no available full licenses; admin cannot toggle is_active until limits are increased. */
  can_activate?: boolean;
}

export interface UserFilters {
  search?: string;
  role?: string;
  status?: string;
  company_id?: string;
  page?: number;
  limit?: number;
}

export interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

// Contact Types
export interface Contact {
  id: string;
  name: string;
  email: string;
  created_at: string;
  created_by: string;
}

export interface CreateContactRequest {
  name: string;
  email: string;
}

// Tag Types
export interface Tag {
  id: string;
  tag_name: string;
  tag_type: 'system' | 'company' | 'user';
  created_by?: string;
  created_at: string;
}

export interface AssetTag extends Tag {
  file_id: string;
}

export interface PolicyTag extends Tag {
  policy_id: string;
}

export interface AddTagRequest {
  tag_name: string;
  tag_type: 'system' | 'company' | 'user';
}

export interface BulkTagOperation {
  file_ids?: string[];
  policy_ids?: string[];
  tag_names: string[];
  tag_type: 'system' | 'company' | 'user';
}

export interface PopularTag {
  name: string;
  type: 'system' | 'company' | 'user';
  usage_count: number;
}

// Company Resources Types
export interface TenantDatabaseRequest {
  connection_string?: string;
  host?: string;
  port?: string;
  user?: string;
  password?: string;
  database?: string;
  sslmode?: string;
}

export interface CompanyResource {
  id: string;
  company_id: string;
  storage_mode: 'shared' | 'self';
  kms_mode: 'shared' | 'self';
  email_mode: 'shared' | 'self';
  database_mode?: 'shared' | 'self';
  tenant_database_provisioned_at?: string | null;
  storage_type?: string;
  kms_type?: string;
  email_provider?: 'resend' | 'mailgun' | 'sendgrid';
  email_from_address?: string;
  email_domain?: string;
  email_api_url?: string;
  credential_source: 'api' | 'infisical-shared' | 'infisical-customer';
  infisical_organization_id?: string;
  infisical_project_id?: string;
  infisical_environment?: string;
  infisical_url?: string;
  infisical_region?: string;
  infisical_zone?: string;
  infisical_slug?: string;
  infisical_storage_credentials_path?: string;
  infisical_kms_credentials_path?: string;
  infisical_email_credentials_path?: string;
  infisical_provisioning_status?: 'pending' | 'provisioning' | 'ready' | 'failed';
  infisical_provisioning_error?: string;
  infisical_credentials_received?: boolean;
  allow_company_admins_resources?: boolean;
  allow_company_admins_spin_up_db?: boolean;
  allow_company_admins_bring_own_db?: boolean;
  allow_company_admins_backup_db?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProvisionInfisicalRequest {
  company_slug: string;
  region: string;
}

export interface InfisicalInstance {
  company_id: string;
  company_slug: string;
  dns_name: string;
  static_ip: string;
  secret_id: string;
  region: string;
  zone: string;
  status: 'pending' | 'provisioning' | 'ready' | 'failed';
}

// ─── Audit Types ─────────────────────────────────────────────────────────────

export interface AuditEvent {
  dt: string;
  message: string;
  level: string;
  event_type: string;
  audit_scope: string;
  actor_id?: string;
  actor_email?: string;
  user_id?: string;
  user_email?: string;
  company_id?: string;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  ip_address?: string;
  details?: Record<string, unknown>;
  [key: string]: unknown; // allow any additional fields BetterStack returns
}

export interface AuditQueryParams {
  event_type?: string;
  event_types?: string[]; // multi-select; sent as comma-separated event_types query
  resource_type?: string;
  search?: string;
  actor_filter?: string;
  resource_id?: string;
  from?: string;
  to?: string;
  before?: string; // pagination cursor (dt of last row)
  company_id?: string; // system endpoint only
  scope?: 'user'; // admin/sysadmin: request personal feed instead of company-wide
}

function buildAuditQueryString(params: AuditQueryParams): string {
  const p = new URLSearchParams();
  if (params.event_types && params.event_types.length > 0) {
    p.set('event_types', params.event_types.join(','));
  } else if (params.event_type) {
    p.set('event_type', params.event_type);
  }
  if (params.resource_type) p.set('resource_type', params.resource_type);
  if (params.search) p.set('search', params.search);
  if (params.actor_filter) p.set('actor_filter', params.actor_filter);
  if (params.resource_id) p.set('resource_id', params.resource_id);
  if (params.from) p.set('from', params.from);
  if (params.to) p.set('to', params.to);
  if (params.before) p.set('before', params.before);
  if (params.company_id) p.set('company_id', params.company_id);
  if (params.scope) p.set('scope', params.scope);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export interface AuditExportRequest {
  from: string;
  to: string;
  format: 'csv' | 'json' | 'pdf';
  event_types?: string[];
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
